import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText, Save, RefreshCcw, AlertCircle, CheckCircle2, Loader2, Code, Globe,
  Building2, Image as ImageIcon, ChevronRight, ChevronDown, Eraser, ExternalLink,
  Tags, PanelTop, Plus, Trash2, Search, Eye, ArrowUp, ArrowDown, LayoutTemplate,
  Bold, Italic, List, ListOrdered, Link as LinkIcon, Heading2, Quote, Menu,
  Folder, FolderOpen, Home,
} from 'lucide-react';
import { translations as BASE_TRANSLATIONS, company as BASE_COMPANY, media as BASE_MEDIA } from '../i18n';
import { CATEGORIES, getCategory } from '../catalog';
import { adminApi } from './AdminAuthContext';
import MediaFolderPane from './MediaFolderPane';
import MediaViewControls, { getMediaGridStyle, useMediaTileSize } from './MediaViewControls';
import {
  HOME_BLUEPRINT_TEMPLATE,
  createHomeBlueprintPage,
  createHomeBlueprintSections,
  getBlueprintSectionLabel,
  normalizeBlueprintSections,
} from '../pageBlueprint';
import {
  LAYOUT_EDITOR_TEMPLATE,
  PAGE_BLOCK_TYPES,
  createLayoutEditorPage,
  createPageBlock,
  getPageBlockLabel,
  normalizePageBlocks,
} from '../pageLayout';

/**
 * Admin Content Editor — sparse overlay on top of the build-time i18n.
 *
 * The form walks the *base* `i18n.js` exports recursively, rendering one
 * editable field per leaf string/array/number. The page stores edits in a
 * sparse mirror; we PUT that mirror to `/api/admin/content` so the
 * frontend can deep-merge it onto the defaults at runtime.
 *
 * Why sparse rather than full: keeps the DB document tiny (only the
 * deltas), and a string left untouched in the UI continues to track the
 * code-shipped default — useful when we ship copy updates.
 */

// Map i18n section keys → public-site anchor IDs (where they're rendered).
// Hero/empty values mean "no specific anchor, just open the homepage top".
// Source of truth: `id:` values in /app/frontend/src/content.js.
const SECTION_ANCHORS = {
  hero:       '',
  trustbar:   '',
  benefits:   'voordelen',
  problem:    '',
  empathy:    '',
  promise:    '',
  products:   'collectie',
  lifestyle:  '',
  werkwijze:  'werkwijze',
  comparison: 'vergelijk',
  reviews:    'getuigenis',
  trust:      'over-ons',
  ctaBlock:   'gratis-advies',
  stalen:     'gratis-stalen',
  faq:        '',
  nav:        '',
  config:     '',
};

const TABS = [
  { id: 'translations', label: 'Texte (NL + DE)', Icon: Globe },
  { id: 'company',      label: 'Firmendaten',      Icon: Building2 },
  { id: 'media',        label: 'Medien',           Icon: ImageIcon },
  { id: 'seo',          label: 'SEO',              Icon: Tags },
  { id: 'pages',        label: 'Seiten',           Icon: PanelTop },
  { id: 'navigation',   label: 'Megamenü',         Icon: Menu },
  { id: 'raw',          label: 'Raw JSON',         Icon: Code },
];

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const isArrayOfPrimitives = (v) => Array.isArray(v) && v.every((x) => typeof x !== 'object' || x === null);
const deepClone = (v) => JSON.parse(JSON.stringify(v));
const normalizePath = (path) => {
  const raw = String(path || '/').trim();
  const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : '/';
};
const slugifyPath = (value) => normalizePath(String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9-_/]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^[-/]+|[-/]+$/g, '') || 'neue-seite');
const mediaParentPath = (path) => {
  const parts = String(path || '').split('/').filter(Boolean);
  parts.pop();
  return parts.join('/');
};
const EMPTY_PICKER_AI = {
  subject_type: '',
  scene_type: '',
  confidence: 0,
  visible_features: [],
  suitable_for: [],
  not_suitable_for: [],
  recommended_pages: [],
  summary: { nl: '', de: '' },
};
const EMPTY_PICKER_META = {
  title: { nl: '', de: '' },
  alt: { nl: '', de: '' },
  description: { nl: '', de: '' },
  tags: [],
  ai: EMPTY_PICKER_AI,
};
const listValue = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
function ensurePickerAi(ai) {
  const source = ai && typeof ai === 'object' && !Array.isArray(ai) ? ai : {};
  return {
    ...EMPTY_PICKER_AI,
    ...source,
    visible_features: listValue(source.visible_features),
    suitable_for: listValue(source.suitable_for),
    not_suitable_for: listValue(source.not_suitable_for),
    recommended_pages: listValue(source.recommended_pages),
    summary: { ...EMPTY_PICKER_AI.summary, ...(source.summary || {}) },
  };
}
function ensurePickerMeta(meta) {
  const ai = ensurePickerAi(meta?.ai);
  return {
    title: { ...EMPTY_PICKER_META.title, ...(meta?.title || {}) },
    alt: { ...EMPTY_PICKER_META.alt, ...(meta?.alt || {}) },
    description: { ...EMPTY_PICKER_META.description, ...(meta?.description || {}) },
    tags: Array.isArray(meta?.tags) ? meta.tags : [],
    ai,
  };
}
function mediaPickerSearchText(item) {
  const meta = ensurePickerMeta(item?.meta);
  const ai = meta.ai;
  return [
    item?.filename,
    item?.path,
    item?.folder,
    meta.title.de,
    meta.title.nl,
    meta.alt.de,
    meta.alt.nl,
    meta.description.de,
    meta.description.nl,
    ...(meta.tags || []),
    ai.subject_type,
    ai.scene_type,
    ai.summary.de,
    ai.summary.nl,
    ...ai.visible_features,
    ...ai.suitable_for,
    ...ai.not_suitable_for,
    ...ai.recommended_pages,
  ].filter(Boolean).join(' ').toLowerCase();
}
function pickerConfidence(ai) {
  const value = Number(ai?.confidence || 0);
  return value ? `${Math.round(value * 100)}%` : '';
}
function PickerAiHint({ meta, compact = false }) {
  const ai = ensurePickerAi(meta?.ai);
  const hasAi = ai.subject_type || ai.scene_type || ai.summary.de || ai.visible_features.length;
  if (!hasAi) return null;
  return (
    <div className={`rounded-xl border border-[#E5E0D2] bg-white ${compact ? 'px-2 py-1.5' : 'p-3'} space-y-2`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-[0.14em] text-[#5C5852] font-semibold">KI-Erkennung</span>
        {pickerConfidence(ai) && <span className="text-[10px] text-[#F26B1A] font-semibold">{pickerConfidence(ai)}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ai.subject_type && <span className="px-2 py-0.5 rounded-full bg-[#1F1D1A] text-white text-[10px] font-semibold">{ai.subject_type}</span>}
        {ai.scene_type && <span className="px-2 py-0.5 rounded-full bg-[#FAF8F2] text-[#1F1D1A] text-[10px] font-semibold">{ai.scene_type}</span>}
      </div>
      {!compact && (ai.summary.de || ai.summary.nl) && (
        <p className="text-[12px] leading-relaxed text-[#5C5852]">{ai.summary.de || ai.summary.nl}</p>
      )}
      {!compact && ai.visible_features.length > 0 && (
        <p className="text-[11px] leading-relaxed text-[#5C5852]">
          Sichtbar: {ai.visible_features.slice(0, 6).join(', ')}
        </p>
      )}
    </div>
  );
}

/**
 * Walk a path like `['nl','hero','title1']` on `obj` returning the leaf
 * (or undefined). Used to look up overlay values + originals side-by-side.
 */
function getAt(obj, path) {
  let cur = obj;
  for (const seg of path) {
    if (cur == null) return undefined;
    cur = cur[seg];
  }
  return cur;
}

/**
 * Set a value at a deep path, creating intermediate objects as needed,
 * and pruning empty objects on the way up if `value === undefined`. The
 * mutation happens on a deep clone — never the original argument.
 */
function setAt(obj, path, value) {
  const next = deepClone(obj || {});
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) {
    const seg = path[i];
    if (cur[seg] == null || typeof cur[seg] !== 'object') {
      cur[seg] = typeof path[i + 1] === 'number' ? [] : {};
    }
    cur = cur[seg];
  }
  const last = path[path.length - 1];
  if (value === undefined) {
    delete cur[last];
    // Walk back up and remove empty containers — keeps overlay tidy.
    for (let i = path.length - 2; i >= 0; i--) {
      const parentPath = path.slice(0, i + 1);
      const parent = getAt(next, parentPath);
      if (isPlainObject(parent) && Object.keys(parent).length === 0) {
        const grand = i === 0 ? next : getAt(next, path.slice(0, i));
        delete grand[path[i]];
      } else break;
    }
  } else {
    cur[last] = value;
  }
  return next;
}

function useMediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/admin/media');
      const next = Array.isArray(data) ? data : [];
      setItems(next.filter((item) => item?.type !== 'pdf'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  return { items, loading, refresh: load };
}

function MediaPicker({ open, onSelect, onClose }) {
  const [q, setQ] = useState('');
  const [path, setPath] = useState('');
  const [mobilePane, setMobilePane] = useState('folders');
  const [tileSize, setTileSize] = useMediaTileSize('horrenmaker.admin.mediaPicker.tileSize');
  const [listing, setListing] = useState({ path: '', parent: null, breadcrumbs: [], folders: [], files: [] });
  const [folderTree, setFolderTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const loadFolder = useCallback(async (nextPath = '') => {
    setLoading(true);
    setError(null);
    setSelected(null);
    try {
      const { data } = await adminApi.get('/admin/media/browse', { params: { path: nextPath } });
      setListing(data || { path: '', parent: null, breadcrumbs: [], folders: [], files: [] });
      setPath(data?.path || '');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Medienpool konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTree = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/admin/media/tree');
      setFolderTree(Array.isArray(data?.folders) ? data.folders : []);
    } catch (e) {
      setFolderTree([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setQ('');
    setMobilePane('folders');
    loadTree();
    loadFolder('');
  }, [open, loadFolder, loadTree]);

  const openFolder = useCallback(async (nextPath) => {
    await loadFolder(nextPath);
    setMobilePane('files');
  }, [loadFolder]);

  const selectFile = useCallback((item) => {
    setSelected(item);
    setMobilePane('details');
  }, []);

  if (!open) return null;
  const query = q.toLowerCase().trim();
  const folders = (listing.folders || []).filter((item) =>
    !query || `${item.name || ''} ${item.path || ''}`.toLowerCase().includes(query)
  );
  const files = (listing.files || [])
    .filter((item) => item?.type !== 'pdf')
    .filter((item) => !query || mediaPickerSearchText(item).includes(query));
  const visibleFiles = path === '' ? [] : files;
  const selectedUrl = selected?.url || '';
  const selectedMeta = ensurePickerMeta(selected?.meta);
  const mediaGridStyle = getMediaGridStyle(tileSize);

  return (
    <div className="fixed inset-0 z-[80] bg-black/45 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-[min(1680px,97vw)] h-[94vh] sm:h-[90vh] rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E5E0D2] flex items-center justify-between gap-3 shrink-0">
          <div>
            <p className="font-display text-[20px] text-[#1F1D1A]">Medienpool</p>
            <p className="text-[12px] text-[#5C5852]">Explorer: links Ordner, Mitte Dateien, rechts Vorschau und Uebernehmen.</p>
          </div>
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-full text-[12px] text-[#5C5852] hover:bg-[#FAF8F2]">Schliessen</button>
        </div>

        <div className="lg:hidden grid grid-cols-3 gap-2 p-3 border-b border-[#E5E0D2] bg-[#FAF8F2] shrink-0">
          {[
            ['folders', 'Ordner'],
            ['files', 'Bilder'],
            ['details', 'Vorschau'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setMobilePane(id)}
              className={`px-3 py-2 rounded-full text-[12px] font-semibold border ${mobilePane === id ? 'bg-[#F26B1A] border-[#F26B1A] text-white' : 'bg-white border-[#E5E0D2] text-[#5C5852]'}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)_340px] min-h-0 flex-1 bg-[#FAF8F2]">
          <MediaFolderPane
            folders={folderTree}
            currentPath={path}
            onOpen={openFolder}
            className={`${mobilePane === 'folders' ? 'flex' : 'hidden'} lg:flex border-r border-[#E5E0D2] bg-white`}
          />

          <section className={`${mobilePane === 'files' ? 'flex' : 'hidden'} lg:flex min-h-0 flex-col bg-white`}>
            <div className="p-4 border-b border-[#E5E0D2] shrink-0 space-y-3">
              <div className="flex flex-col 2xl:flex-row 2xl:items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold">Aktueller Ordner</p>
                  <p className="font-mono text-[12px] text-[#1F1D1A] mt-1 truncate">/medienpool{path ? `/${path}` : ''}</p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <MediaViewControls value={tileSize} onChange={setTileSize} />
                  {path && (
                    <button
                      type="button"
                      onClick={() => openFolder(mediaParentPath(path))}
                      className="shrink-0 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-[#FAF8F2] text-[#5C5852] text-[12px] font-semibold hover:text-[#F26B1A]"
                    >
                      <Folder className="w-4 h-4" /> Hoch
                    </button>
                  )}
                </div>
              </div>
              <label className="relative block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5852]" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="In diesem Ordner suchen"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]"
                />
              </label>
            </div>

            <div className="p-4 overflow-y-auto min-h-0 flex-1">
              {loading ? (
                <div className="p-10 text-center text-[#5C5852]"><Loader2 className="inline w-5 h-5 animate-spin" /></div>
              ) : error ? (
                <p className="p-10 text-center text-[14px] text-[#C03A1A] bg-[#FAF8F2] rounded-xl">{error}</p>
              ) : folders.length === 0 && visibleFiles.length === 0 ? (
                <p className="p-10 text-center text-[14px] text-[#5C5852] bg-[#FAF8F2] rounded-xl">Keine Medien gefunden.</p>
              ) : (
                <div className="space-y-5">
              {path && (
                <div className="xl:hidden flex items-center gap-2 overflow-x-auto pb-1">
                  <button type="button" onClick={() => openFolder('')} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF8F2] text-[#1F1D1A] text-[12px] font-semibold hover:text-[#F26B1A]">
                    <Home className="w-3.5 h-3.5" /> /medienpool
                  </button>
                  {(listing.breadcrumbs || []).map((crumb) => (
                    <button key={crumb.path} type="button" onClick={() => openFolder(crumb.path)} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF8F2] text-[#1F1D1A] text-[12px] font-semibold hover:text-[#F26B1A]">
                      <ChevronRight className="w-3.5 h-3.5" /> {crumb.name}
                    </button>
                  ))}
                </div>
              )}

              {folders.length > 0 && (
                <section>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold mb-2">Ordner</p>
                  <div className="grid gap-3" style={mediaGridStyle}>
                    {folders.map((folder) => (
                      <button
                        key={folder.path}
                        type="button"
                        onClick={() => openFolder(folder.path)}
                        className="group text-left rounded-xl border border-[#E5E0D2] bg-white hover:border-[#F26B1A] transition-colors p-4 min-h-[104px] flex items-start gap-3"
                      >
                        <span className="w-11 h-11 rounded-xl bg-[#F26B1A]/10 text-[#F26B1A] inline-flex items-center justify-center shrink-0 group-hover:bg-[#F26B1A] group-hover:text-white transition-colors">
                          <FolderOpen className="w-5 h-5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[13px] font-semibold text-[#1F1D1A] truncate">{folder.name}</span>
                          <span className="block text-[10px] text-[#5C5852] font-mono truncate mt-1" title={folder.path}>{folder.path}</span>
                          <span className="inline-flex mt-2 text-[10.5px] font-semibold text-[#F26B1A]">Oeffnen</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {visibleFiles.length > 0 && (
                <section>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold mb-2">Bilder</p>
                  <div className="grid gap-3" style={mediaGridStyle}>
                    {visibleFiles.map((item) => {
                      const meta = ensurePickerMeta(item.meta);
                      const ai = meta.ai;
                      return (
                        <button
                          key={item.path || item.filename}
                          type="button"
                          onClick={() => selectFile(item)}
                          onDoubleClick={() => onSelect(item.url)}
                          className={`group text-left rounded-xl overflow-hidden border bg-white transition-colors ${selected?.path === item.path ? 'border-[#F26B1A] ring-2 ring-[#F26B1A]/20' : 'border-[#E5E0D2] hover:border-[#F26B1A]'}`}
                        >
                          <div className="aspect-square bg-[#FAF8F2] overflow-hidden">
                            <img src={item.url} alt={meta.alt.de || meta.alt.nl || item.filename} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" loading="lazy" />
                          </div>
                          <div className="p-2">
                            <p className="font-mono text-[10px] text-[#1F1D1A] truncate" title={item.path || item.filename}>{item.filename}</p>
                            <p className="text-[10px] text-[#5C5852] mt-0.5 truncate" title={item.path}>/{item.path}</p>
                            {(ai.subject_type || ai.scene_type) && (
                              <p className="text-[10px] text-[#F26B1A] font-semibold mt-1 truncate" title={`${ai.subject_type || ''} ${ai.scene_type || ''}`.trim()}>
                                {[ai.subject_type, ai.scene_type].filter(Boolean).join(' / ')}
                              </p>
                            )}
                            <p className="text-[10.5px] text-[#F26B1A] font-semibold mt-1">Auswaehlen</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
              )}
            </div>
          </section>

          <aside className={`${mobilePane === 'details' ? 'block' : 'hidden'} lg:block lg:col-span-2 xl:col-span-1 min-h-0 border-l border-[#E5E0D2] bg-[#FAF8F2] p-4 overflow-y-auto`}>
            {selected ? (
              <div className="space-y-4">
                <div className="rounded-2xl border border-[#E5E0D2] bg-white overflow-hidden">
                  <div className="aspect-[4/3] bg-white">
                    {selectedUrl ? <img src={selectedUrl} alt={selectedMeta.alt.de || selectedMeta.alt.nl || selected.filename} className="w-full h-full object-contain" /> : <ImageIcon className="w-10 h-10 m-auto text-[#5C5852]" />}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold">Ausgewaehltes Bild</p>
                  <p className="mt-2 font-semibold text-[14px] text-[#1F1D1A] break-words">{selected.filename}</p>
                  <p className="mt-2 font-mono text-[11px] text-[#5C5852] break-all">/{selected.path}</p>
                </div>
                <PickerAiHint meta={selectedMeta} />
                <button
                  type="button"
                  onClick={() => onSelect(selected.url)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A]"
                >
                  <ImageIcon className="w-4 h-4" /> Bild uebernehmen
                </button>
              </div>
            ) : (
              <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center rounded-2xl border border-dashed border-[#E5E0D2] bg-white p-6">
                <ImageIcon className="w-9 h-9 text-[#F26B1A]" />
                <p className="mt-3 font-display text-[20px] text-[#1F1D1A]">Vorschau</p>
                <p className="mt-2 text-[12.5px] text-[#5C5852] leading-relaxed">Waehle in der Mitte ein Bild aus. Doppelklick uebernimmt es sofort.</p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function MediaUrlField({ path, base = '', overlay, onChange, testId, mediaItems, mediaLoading }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const o = getAt(overlay, path);
  const overridden = o !== undefined;
  const value = overridden ? o : base;
  const reset = () => onChange(setAt(overlay, path, undefined));
  const update = (v) => {
    if (v === base) onChange(setAt(overlay, path, undefined));
    else onChange(setAt(overlay, path, v));
  };
  const preview = value || '';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <code className="text-[10.5px] text-[#5C5852] tracking-tight">{path.join('.')}</code>
        {overridden && (
          <button type="button" onClick={reset} data-testid={`${testId}-reset`}
            className="inline-flex items-center gap-0.5 text-[10.5px] text-[#F26B1A] hover:text-[#D85A0A]">
            <Eraser className="w-3 h-3" /> Reset
          </button>
        )}
      </div>
      <div className="flex gap-2 items-start">
        <div className="w-16 h-16 rounded-lg border border-[#E5E0D2] bg-[#FAF8F2] overflow-hidden shrink-0 flex items-center justify-center">
          {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-[#5C5852]" />}
        </div>
        <div className="flex-1 min-w-0">
          <input
            data-testid={testId}
            type="text"
            value={value ?? ''}
            onChange={(e) => update(e.target.value)}
            className={`w-full px-2 py-1.5 rounded border ${overridden ? 'border-[#F26B1A] bg-[#FFF8F0]' : 'border-[#E5E0D2]'} text-[13px]`}
          />
          <div className="flex items-center gap-2 mt-2">
            <button type="button" onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1F1D1A] text-white text-[11px] font-semibold hover:bg-[#F26B1A] transition-colors">
              <ImageIcon className="w-3.5 h-3.5" /> Aus Medienpool
            </button>
            {preview && (
              <a href={preview} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#5C5852] hover:text-[#F26B1A]">
                <Eye className="w-3.5 h-3.5" /> Vorschau
              </a>
            )}
          </div>
        </div>
      </div>
      <MediaPicker
        open={pickerOpen}
        items={mediaItems}
        loading={mediaLoading}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => { update(url); setPickerOpen(false); }}
      />
    </div>
  );
}

function RichHtmlEditor({ value, onChange, testId }) {
  const ref = useRef(null);
  const [source, setSource] = useState(false);
  const [sourceValue, setSourceValue] = useState(value || '');

  useEffect(() => {
    if (!source && ref.current && document.activeElement !== ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
    if (source) setSourceValue(value || '');
  }, [value, source]);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };
  const cmd = (command, arg = null) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    emit();
  };
  const setBlock = (tag) => cmd('formatBlock', tag);
  const addLink = () => {
    const url = window.prompt('Link-URL');
    if (url) cmd('createLink', url);
  };
  const clear = () => {
    cmd('removeFormat');
    cmd('unlink');
  };

  const buttons = [
    { title: 'Fett', Icon: Bold, action: () => cmd('bold') },
    { title: 'Kursiv', Icon: Italic, action: () => cmd('italic') },
    { title: 'H2', Icon: Heading2, action: () => setBlock('h2') },
    { title: 'Zitat', Icon: Quote, action: () => setBlock('blockquote') },
    { title: 'Liste', Icon: List, action: () => cmd('insertUnorderedList') },
    { title: 'Nummerierte Liste', Icon: ListOrdered, action: () => cmd('insertOrderedList') },
    { title: 'Link', Icon: LinkIcon, action: addLink },
    { title: 'Format entfernen', Icon: Eraser, action: clear },
  ];

  return (
    <div data-testid={testId} className="rounded-xl border-2 border-[#E5E0D2] bg-white overflow-hidden focus-within:border-[#F26B1A]">
      <div className="px-2 py-2 border-b border-[#E5E0D2] bg-[#FAF8F2] flex items-center gap-1 flex-wrap">
        {buttons.map(({ title, Icon, action }) => (
          <button key={title} type="button" title={title} onClick={action}
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-[#5C5852] hover:bg-white hover:text-[#F26B1A]">
            <Icon className="w-4 h-4" />
          </button>
        ))}
        <span className="w-px h-5 bg-[#E5E0D2] mx-1" />
        <button type="button" onClick={() => setSource((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold ${source ? 'bg-[#1F1D1A] text-white' : 'text-[#5C5852] hover:bg-white'}`}>
          <Code className="w-3.5 h-3.5" /> HTML
        </button>
      </div>
      {source ? (
        <textarea
          value={sourceValue}
          onChange={(e) => { setSourceValue(e.target.value); onChange(e.target.value); }}
          rows={14}
          spellCheck={false}
          className="w-full p-4 text-[13px] font-mono leading-relaxed focus:outline-none resize-y min-h-[280px]"
        />
      ) : (
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          className="min-h-[280px] p-4 text-[15px] leading-relaxed focus:outline-none prose-like"
        />
      )}
    </div>
  );
}


// ============================================================================
// Leaf editors
// ============================================================================

function StringField({ path, base, overlay, onChange, multiline = false, testId }) {
  const o = getAt(overlay, path);
  const overridden = o !== undefined;
  const value = overridden ? o : base;
  const reset = () => onChange(setAt(overlay, path, undefined));
  const update = (v) => {
    // If user typed the exact default, treat it as "no override" — keeps
    // future code-side copy updates flowing through automatically.
    if (v === base) onChange(setAt(overlay, path, undefined));
    else onChange(setAt(overlay, path, v));
  };
  const cls = `w-full px-2 py-1.5 rounded border ${overridden ? 'border-[#F26B1A] bg-[#FFF8F0]' : 'border-[#E5E0D2]'} text-[13px]`;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <code className="text-[10.5px] text-[#5C5852] tracking-tight">{path.join('.')}</code>
        {overridden && (
          <button type="button" onClick={reset}
            data-testid={`${testId}-reset`}
            className="inline-flex items-center gap-0.5 text-[10.5px] text-[#F26B1A] hover:text-[#D85A0A]">
            <Eraser className="w-3 h-3" /> Reset
          </button>
        )}
      </div>
      {multiline ? (
        <textarea
          data-testid={testId} rows={2}
          value={value ?? ''}
          onChange={(e) => update(e.target.value)}
          className={`${cls} resize-none`}
        />
      ) : (
        <input
          data-testid={testId} type="text"
          value={value ?? ''}
          onChange={(e) => update(e.target.value)}
          className={cls}
        />
      )}
    </div>
  );
}

function ArrayField({ path, base, overlay, onChange, testId }) {
  const o = getAt(overlay, path);
  const overridden = Array.isArray(o);
  const value = overridden ? o : base;
  const reset = () => onChange(setAt(overlay, path, undefined));
  const update = (next) => {
    if (JSON.stringify(next) === JSON.stringify(base)) onChange(setAt(overlay, path, undefined));
    else onChange(setAt(overlay, path, next));
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <code className="text-[10.5px] text-[#5C5852] tracking-tight">{path.join('.')} <span className="text-[#5C5852]/60">({value.length} Items)</span></code>
        {overridden && (
          <button type="button" onClick={reset} data-testid={`${testId}-reset`}
            className="inline-flex items-center gap-0.5 text-[10.5px] text-[#F26B1A] hover:text-[#D85A0A]">
            <Eraser className="w-3 h-3" /> Reset
          </button>
        )}
      </div>
      <ul className="space-y-1">
        {value.map((v, i) => (
          <li key={`${path.join('.')}-${i}`} className="flex items-center gap-1.5">
            <span className="w-5 text-right text-[10.5px] text-[#5C5852]/60 tabular-nums">{i + 1}.</span>
            <input type="text" value={v ?? ''}
              data-testid={`${testId}-${i}`}
              onChange={(e) => { const n = [...value]; n[i] = e.target.value; update(n); }}
              className={`flex-1 px-2 py-1 rounded border ${overridden ? 'border-[#F26B1A] bg-[#FFF8F0]' : 'border-[#E5E0D2]'} text-[12.5px]`} />
            <button type="button" onClick={() => { const n = [...value]; n.splice(i, 1); update(n); }}
              className="px-1.5 text-[10.5px] text-[#5C5852] hover:text-[#C03A1A]">×</button>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => update([...value, ''])}
        data-testid={`${testId}-add`}
        className="text-[10.5px] text-[#F26B1A] hover:text-[#D85A0A] font-semibold">+ Eintrag</button>
    </div>
  );
}

function MediaArrayField({ path, base, overlay, onChange, testId, mediaItems, mediaLoading }) {
  const [pickerIndex, setPickerIndex] = useState(null);
  const o = getAt(overlay, path);
  const overridden = Array.isArray(o);
  const value = overridden ? o : base;
  const reset = () => onChange(setAt(overlay, path, undefined));
  const update = (next) => {
    if (JSON.stringify(next) === JSON.stringify(base)) onChange(setAt(overlay, path, undefined));
    else onChange(setAt(overlay, path, next));
  };
  const updateItem = (index, nextValue) => {
    const next = [...value];
    next[index] = nextValue;
    update(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <code className="text-[10.5px] text-[#5C5852] tracking-tight">
          {path.join('.')} <span className="text-[#5C5852]/60">({value.length} Items)</span>
        </code>
        {overridden && (
          <button type="button" onClick={reset} data-testid={`${testId}-reset`}
            className="inline-flex items-center gap-0.5 text-[10.5px] text-[#F26B1A] hover:text-[#D85A0A]">
            <Eraser className="w-3 h-3" /> Reset
          </button>
        )}
      </div>
      <ul className="space-y-2">
        {value.map((v, i) => {
          const preview = v || '';
          return (
            <li key={`${path.join('.')}-${i}`} className="grid grid-cols-[22px_52px_minmax(0,1fr)_auto] gap-2 items-center">
              <span className="text-right text-[10.5px] text-[#5C5852]/60 tabular-nums">{i + 1}.</span>
              <div className="w-12 h-12 rounded-lg border border-[#E5E0D2] bg-[#FAF8F2] overflow-hidden flex items-center justify-center">
                {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-[#5C5852]" />}
              </div>
              <div className="min-w-0">
                <input type="text" value={v ?? ''}
                  data-testid={`${testId}-${i}`}
                  onChange={(e) => updateItem(i, e.target.value)}
                  className={`w-full px-2 py-1 rounded border ${overridden ? 'border-[#F26B1A] bg-[#FFF8F0]' : 'border-[#E5E0D2]'} text-[12.5px]`} />
                <div className="flex items-center gap-2 mt-1.5">
                  <button type="button" onClick={() => setPickerIndex(i)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1F1D1A] text-white text-[10.5px] font-semibold hover:bg-[#F26B1A] transition-colors">
                    <ImageIcon className="w-3.5 h-3.5" /> Aus Medienpool
                  </button>
                  {preview && (
                    <a href={preview} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10.5px] text-[#5C5852] hover:text-[#F26B1A]">
                      <Eye className="w-3.5 h-3.5" /> Vorschau
                    </a>
                  )}
                </div>
              </div>
              <button type="button" onClick={() => { const n = [...value]; n.splice(i, 1); update(n); }}
                className="px-1.5 text-[13px] text-[#5C5852] hover:text-[#C03A1A]">x</button>
            </li>
          );
        })}
      </ul>
      <button type="button" onClick={() => update([...value, ''])}
        data-testid={`${testId}-add`}
        className="text-[10.5px] text-[#F26B1A] hover:text-[#D85A0A] font-semibold">+ Bild</button>
      <MediaPicker
        open={pickerIndex !== null}
        items={mediaItems}
        loading={mediaLoading}
        onClose={() => setPickerIndex(null)}
        onSelect={(url) => {
          if (pickerIndex !== null) updateItem(pickerIndex, url);
          setPickerIndex(null);
        }}
      />
    </div>
  );
}


// ============================================================================
// Recursive walker — renders one collapsible Section per top-level key
// ============================================================================

function Section({ title, baseObj, overlay, onChange, pathPrefix = [], openByDefault = false, previewHref = null }) {
  const [open, setOpen] = useState(openByDefault);
  // Count nested overrides for the section header badge.
  const overrideCount = useMemo(() => {
    let n = 0;
    function walk(b, o, p) {
      if (!isPlainObject(b)) return;
      for (const k of Object.keys(b)) {
        const np = [...p, k];
        const bv = b[k];
        const ov = getAt(o, np);
        if (ov !== undefined) {
          if (isPlainObject(bv)) walk(bv, o, np);
          else n += 1;
        }
      }
    }
    walk(baseObj, overlay, pathPrefix);
    return n;
  }, [baseObj, overlay, pathPrefix]);

  return (
    <section className="bg-white rounded-2xl border border-[#E5E0D2] overflow-hidden">
      <div className="w-full px-5 py-3 bg-[#FAF8F2] border-b border-[#E5E0D2] flex items-center justify-between gap-2">
        <button type="button" onClick={() => setOpen((s) => !s)}
          data-testid={`content-section-${pathPrefix.join('.') || title}`}
          className="flex-1 flex items-center gap-2 text-left">
          {open ? <ChevronDown className="w-4 h-4 text-[#F26B1A]" /> : <ChevronRight className="w-4 h-4 text-[#5C5852]" />}
          <p className="font-display text-[15px] text-[#1F1D1A]">{title}</p>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {overrideCount > 0 && (
            <span className="text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F26B1A]/10 text-[#F26B1A]">
              {overrideCount} Override{overrideCount === 1 ? '' : 's'}
            </span>
          )}
          {previewHref && (
            <a href={previewHref} target="_blank" rel="noopener noreferrer"
              data-testid={`content-section-preview-${pathPrefix.join('.')}`}
              title="Live-Vorschau in neuem Tab"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold text-[#5C5852] hover:bg-white hover:text-[#F26B1A] transition-colors">
              <ExternalLink className="w-3 h-3" /> Vorschau
            </a>
          )}
        </div>
      </div>
      {open && (
        <div className="p-5 space-y-3">
          <Walker baseObj={baseObj} overlay={overlay} onChange={onChange} pathPrefix={pathPrefix} />
        </div>
      )}
    </section>
  );
}

function Walker({ baseObj, overlay, onChange, pathPrefix = [], mediaMode = false, mediaItems = [], mediaLoading = false }) {
  if (!isPlainObject(baseObj)) return null;
  const entries = Object.entries(baseObj);
  return (
    <>
      {entries.map(([k, v]) => {
        const path = [...pathPrefix, k];
        const testId = `field-${path.join('-')}`;
        if (isPlainObject(v)) {
          // We deliberately call Walker via React.createElement rather than
          // <Walker /> JSX. Some dev-time babel plugins (notably the
          // emergent visual-edits transform) infinite-loop when a JSX
          // element self-references its enclosing component definition.
          return (
            <details key={path.join('.')} className="pl-3 border-l-2 border-[#FAF8F2]">
              <summary className="text-[11.5px] uppercase tracking-wider font-semibold text-[#5C5852] cursor-pointer mb-2">{k}</summary>
              <div className="space-y-3 pl-2 pt-1">
                {React.createElement(Walker, { baseObj: v, overlay, onChange, pathPrefix: path, mediaMode, mediaItems, mediaLoading })}
              </div>
            </details>
          );
        }
        if (isArrayOfPrimitives(v)) {
          if (mediaMode) {
            return (
              <MediaArrayField
                key={path.join('.')}
                path={path}
                base={v}
                overlay={overlay}
                onChange={onChange}
                testId={testId}
                mediaItems={mediaItems}
                mediaLoading={mediaLoading}
              />
            );
          }
          return <ArrayField key={path.join('.')} path={path} base={v} overlay={overlay} onChange={onChange} testId={testId} />;
        }
        if (typeof v === 'string') {
          if (mediaMode) {
            return (
              <MediaUrlField
                key={path.join('.')}
                path={path}
                base={v}
                overlay={overlay}
                onChange={onChange}
                testId={testId}
                mediaItems={mediaItems}
                mediaLoading={mediaLoading}
              />
            );
          }
          const multiline = v.length > 80 || v.includes('\n');
          return <StringField key={path.join('.')} path={path} base={v} overlay={overlay} onChange={onChange} multiline={multiline} testId={testId} />;
        }
        return null;
      })}
    </>
  );
}


// ============================================================================
// Tab bodies
// ============================================================================

function TranslationsTab({ overlay, onChange }) {
  const [lang, setLang] = useState('nl');
  const base = BASE_TRANSLATIONS[lang];
  const sectionKeys = Object.keys(base);
  const previewBase = `/${lang === 'de' ? '?lang=de' : ''}`;

  return (
    <div className="space-y-4" data-testid="content-tab-translations">
      <div className="bg-white rounded-xl border border-[#E5E0D2] p-1 inline-flex gap-1">
        {['nl', 'de'].map((l) => (
          <button key={l} type="button" onClick={() => setLang(l)}
            data-testid={`content-lang-${l}`}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${
              lang === l ? 'bg-[#F26B1A] text-white' : 'text-[#5C5852] hover:bg-[#FAF8F2]'
            }`}>
            {l === 'nl' ? '🇳🇱 Niederländisch' : '🇩🇪 Deutsch'}
          </button>
        ))}
      </div>
      {sectionKeys.map((k) => {
        const anchor = SECTION_ANCHORS[k];
        // Build full preview URL — origin-prefixed so the new tab routes
        // to the public site, not /admin. anchor==='' → just open homepage.
        const previewHref = typeof anchor === 'string'
          ? `${window.location.origin}/${anchor ? `#${anchor}` : ''}`
          : null;
        return (
          <Section key={`${lang}-${k}`}
            title={k.toUpperCase()}
            baseObj={base[k]}
            overlay={overlay}
            onChange={onChange}
            pathPrefix={['translations', lang, k]}
            previewHref={previewHref} />
        );
      })}
    </div>
  );
}

function CompanyTab({ overlay, onChange }) {
  return (
    <div className="space-y-3" data-testid="content-tab-company">
      <section className="bg-white rounded-2xl border border-[#E5E0D2] p-5 space-y-3">
        <Walker baseObj={BASE_COMPANY} overlay={overlay} onChange={onChange} pathPrefix={['company']} />
      </section>
    </div>
  );
}

function MediaTab({ overlay, onChange }) {
  const { items, loading, refresh } = useMediaLibrary();
  return (
    <div className="space-y-3" data-testid="content-tab-media">
      <section className="bg-white rounded-2xl border border-[#E5E0D2] p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[11.5px] text-[#5C5852]">
            Bilder koennen direkt aus dem Medienpool gewaehlt werden. Die URL wird automatisch eingesetzt.
          </p>
          <button type="button" onClick={refresh}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FAF8F2] text-[#5C5852] text-[11px] font-semibold hover:text-[#F26B1A]">
            <RefreshCcw className="w-3.5 h-3.5" /> Aktualisieren
          </button>
        </div>
        <Walker baseObj={BASE_MEDIA} overlay={overlay} onChange={onChange} pathPrefix={['media']}
          mediaMode mediaItems={items} mediaLoading={loading} />
      </section>
    </div>
  );
}

function buildSeoRoutes() {
  const routes = [
    { path: '/', label: 'Startseite' },
    { path: '/configurator', label: 'Konfigurator' },
    { path: '/gratis-stalen', label: 'Gratis Stalen' },
    { path: '/offerte', label: 'Offerte' },
    { path: '/contact', label: 'Kontakt' },
    { path: '/privacy', label: 'Privacy' },
    { path: '/voorwaarden', label: 'Voorwaarden' },
    { path: '/impressum', label: 'Impressum' },
  ];
  for (const category of CATEGORIES) {
    const cat = getCategory('nl', category);
    if (!cat) continue;
    routes.push({ path: `/${category}`, label: cat.name });
    for (const product of cat.variants || []) {
      routes.push({ path: `/${category}/${product.slug}`, label: `${cat.name} / ${product.name}` });
    }
  }
  return routes;
}

function SeoTextInput({ value, onChange, placeholder, multiline = false, testId }) {
  const cls = "w-full px-3 py-2 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[13px]";
  return multiline ? (
    <textarea data-testid={testId} value={value || ''} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} rows={3} className={`${cls} resize-none`} />
  ) : (
    <input data-testid={testId} type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} className={cls} />
  );
}

function SeoTab({ overlay, onChange }) {
  const [lang, setLang] = useState('nl');
  const [selectedPath, setSelectedPath] = useState('/');
  const { items, loading } = useMediaLibrary();
  const routes = useMemo(() => buildSeoRoutes(), []);
  const current = getAt(overlay, ['seo', selectedPath, lang]) || {};
  const update = (key, value) => {
    const clean = value === '' || value === false || value == null ? undefined : value;
    onChange(setAt(overlay, ['seo', selectedPath, lang, key], clean));
  };
  return (
    <div data-testid="content-tab-seo" className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      <aside className="lg:col-span-4 bg-white rounded-2xl border border-[#E5E0D2] overflow-hidden">
        <div className="p-3 border-b border-[#E5E0D2]">
          <p className="font-display text-[17px] text-[#1F1D1A]">Seiten</p>
          <p className="text-[11.5px] text-[#5C5852] mt-1">SEO pro URL bearbeiten.</p>
        </div>
        <div className="max-h-[64vh] overflow-y-auto p-2 space-y-1">
          {routes.map((route) => (
            <button key={route.path} type="button" onClick={() => setSelectedPath(route.path)}
              className={`w-full text-left px-3 py-2 rounded-lg text-[12.5px] transition-colors ${selectedPath === route.path ? 'bg-[#F26B1A] text-white' : 'hover:bg-[#FAF8F2] text-[#1F1D1A]'}`}>
              <span className="block font-semibold truncate">{route.label}</span>
              <span className={`block font-mono text-[10.5px] truncate ${selectedPath === route.path ? 'text-white/70' : 'text-[#5C5852]'}`}>{route.path}</span>
            </button>
          ))}
        </div>
      </aside>

      <section className="lg:col-span-8 bg-white rounded-2xl border border-[#E5E0D2] p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-display text-[19px] text-[#1F1D1A]">{selectedPath}</p>
            <p className="text-[11.5px] text-[#5C5852]">Title, Meta-Description, OG-Bild und Indexierung.</p>
          </div>
          <div className="bg-[#FAF8F2] rounded-xl p-1 inline-flex gap-1">
            {['nl', 'de'].map((l) => (
              <button key={l} type="button" onClick={() => setLang(l)}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold ${lang === l ? 'bg-[#F26B1A] text-white' : 'text-[#5C5852]'}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">SEO Title</span>
          <SeoTextInput value={current.title} onChange={(v) => update('title', v)} placeholder="Seitentitel fuer Google" testId="seo-title" />
        </label>
        <label className="block">
          <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Meta Description</span>
          <SeoTextInput value={current.description} onChange={(v) => update('description', v)} multiline placeholder="Kurze Beschreibung fuer Suchergebnisse" testId="seo-description" />
          <span className="block text-[10.5px] text-[#5C5852] mt-1">{(current.description || '').length} Zeichen</span>
        </label>
        <div>
          <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Social/OG Bild</span>
          <MediaUrlField path={['seo', selectedPath, lang, 'image']} base="" overlay={overlay} onChange={onChange}
            testId="seo-image" mediaItems={items} mediaLoading={loading} />
        </div>
        <label className="inline-flex items-center gap-2 text-[13px] text-[#1F1D1A]">
          <input type="checkbox" checked={current.noindex === true} onChange={(e) => update('noindex', e.target.checked)}
            className="w-4 h-4 accent-[#F26B1A]" />
          Seite auf noindex setzen
        </label>
      </section>
    </div>
  );
}

const BLUEPRINT_SECTION_FIELDS = {
  hero: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['h1', 0], label: 'Headline Zeile 1' },
    { path: ['h1', 1], label: 'Headline Zeile 2' },
    { path: ['sub'], label: 'Subline', multiline: true },
    { path: ['mediaSrc'], label: 'Hero-Bild', media: true },
    { path: ['mediaFallback'], label: 'Fallback-Bild', media: true },
    { path: ['primaryLabel'], label: 'Primaerer Button' },
    { path: ['whatsappLabel'], label: 'WhatsApp Button' },
  ],
  heroMosquito: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['h1', 0], label: 'Headline Zeile 1' },
    { path: ['h1', 1], label: 'Headline Zeile 2' },
    { path: ['sub'], label: 'Subline', multiline: true },
    { path: ['mediaSrc'], label: 'Hero-Bild', media: true },
    { path: ['mediaFallback'], label: 'Fallback-Bild', media: true },
    { path: ['primaryLabel'], label: 'Primaerer Button' },
    { path: ['whatsappLabel'], label: 'WhatsApp Button' },
  ],
  problem: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['headline'], label: 'Headline' },
    { path: ['headlineAccent'], label: 'Akzent' },
    { path: ['body'], label: 'Text', multiline: true },
    { path: ['mediaSrc'], label: 'Bild', media: true },
  ],
  trustBar: [
    { path: ['items'], label: 'Trustbar-Texte', lines: true, rows: 7, fullWidth: true },
  ],
  benefits: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
    { path: ['sub'], label: 'Subline', multiline: true },
  ],
  empathy: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['headline'], label: 'Titel' },
    { path: ['headlineAccent'], label: 'Akzent' },
    { path: ['body'], label: 'Text', multiline: true },
    { path: ['mediaSrc'], label: 'Bild', media: true },
  ],
  promise: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
    { path: ['body'], label: 'Text', multiline: true },
    { path: ['mediaSrc'], label: 'Bild', media: true },
  ],
  products: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
  ],
  typeCompare: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
    { path: ['sub'], label: 'Subline', multiline: true },
    { path: ['ctaLabel'], label: 'Button' },
    { path: ['ctaHint'], label: 'Button-Hinweis' },
  ],
  lifestyle: [
    { path: ['title'], label: 'Titel' },
    { path: ['sub'], label: 'Subline', multiline: true },
    { path: ['hint'], label: 'Hinweis' },
    { path: ['mediaSrc'], label: 'Bild', media: true },
  ],
  werkwijze: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
  ],
  comparison: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
    { path: ['sub'], label: 'Subline', multiline: true },
    { path: ['labelBefore'], label: 'Label vorher' },
    { path: ['labelAfter'], label: 'Label nachher' },
    { path: ['captionBefore'], label: 'Titel vorher' },
    { path: ['captionAfter'], label: 'Titel nachher' },
    { path: ['linkBefore'], label: 'Link vorher' },
    { path: ['linkAfter'], label: 'Link nachher' },
    { path: ['hint'], label: 'Mobile Hinweis' },
    { path: ['imageBefore'], label: 'Bild vorher', media: true },
    { path: ['imageAfter'], label: 'Bild nachher', media: true },
  ],
  sample: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
    { path: ['body'], label: 'Text', multiline: true },
    { path: ['mediaSrc'], label: 'Bild', media: true },
  ],
  sampleKit: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
    { path: ['body'], label: 'Text', multiline: true },
    { path: ['imageSrc'], label: 'Bild', media: true },
  ],
  bigQuote: [
    { path: ['quote'], label: 'Zitat', multiline: true },
    { path: ['author'], label: 'Autor' },
    { path: ['role'], label: 'Rolle/Ort' },
  ],
  reviews: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
  ],
  about: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
    { path: ['mediaSrc'], label: 'Bild', media: true },
  ],
  trustDeep: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
    { path: ['titleAfter'], label: 'Titel Akzent' },
    { path: ['body'], label: 'Text', multiline: true },
    { path: ['mediaSrc'], label: 'Bild', media: true },
  ],
  leadForm: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['headline'], label: 'Headline' },
    { path: ['sub'], label: 'Subline', multiline: true },
    { path: ['submitLabel'], label: 'Button' },
  ],
  faq: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['title'], label: 'Titel' },
  ],
  finalCta: [
    { path: ['eyebrow'], label: 'Eyebrow' },
    { path: ['headline'], label: 'Headline' },
    { path: ['sub'], label: 'Subline', multiline: true },
    { path: ['bgImage'], label: 'Hintergrundbild', media: true },
  ],
};

const BLUEPRINT_SECTION_REPEATERS = {
  benefits: [
    {
      path: ['items'],
      label: 'Vorteils-Karten',
      addLabel: 'Vorteil',
      emptyItem: { image: '', icon: 'check', title: 'Neuer Vorteil', sub: '' },
      fields: [
        { path: ['image'], label: 'Bild', media: true },
        { path: ['icon'], label: 'Icon' },
        { path: ['title'], label: 'Titel' },
        { path: ['sub'], label: 'Text', multiline: true },
      ],
    },
  ],
  products: [
    {
      path: ['items'],
      label: 'Produkt-Karten',
      addLabel: 'Produkt',
      emptyItem: { key: 'neu', name: 'Neues Produkt', badge: '', scene: '', desc: '', image: '', imageHover: '', href: '', ctaLabel: '' },
      fields: [
        { path: ['key'], label: 'Key' },
        { path: ['name'], label: 'Name' },
        { path: ['badge'], label: 'Badge' },
        { path: ['scene'], label: 'Szenen-Text', multiline: true },
        { path: ['desc'], label: 'Beschreibung', multiline: true },
        { path: ['image'], label: 'Bild', media: true },
        { path: ['imageHover'], label: 'Hover-Bild', media: true },
        { path: ['href'], label: 'Link' },
        { path: ['ctaLabel'], label: 'Button-Text' },
      ],
    },
  ],
  typeCompare: [
    {
      path: ['items'],
      label: 'Vergleichs-Karten',
      addLabel: 'Karte',
      emptyItem: { key: 'neu', name: 'Neue Karte', tag: '', tagline: '', desc: '', forWho: '', pros: [], cons: [], badge: '', image: '' },
      fields: [
        { path: ['key'], label: 'Key' },
        { path: ['name'], label: 'Name' },
        { path: ['tag'], label: 'Tag' },
        { path: ['badge'], label: 'Badge' },
        { path: ['tagline'], label: 'Kurzzeile' },
        { path: ['desc'], label: 'Beschreibung', multiline: true },
        { path: ['forWho'], label: 'Fuer wen?', multiline: true },
        { path: ['pros'], label: 'Vorteile', lines: true },
        { path: ['cons'], label: 'Einschraenkungen', lines: true },
        { path: ['image'], label: 'Bild', media: true },
      ],
    },
  ],
  werkwijze: [
    {
      path: ['items'],
      legacyPath: ['steps'],
      label: 'Ablauf-Schritte',
      addLabel: 'Schritt',
      emptyItem: { n: '01', t: 'Neuer Schritt', d: '', image: '' },
      fields: [
        { path: ['n'], label: 'Nummer' },
        { path: ['t'], label: 'Titel' },
        { path: ['d'], label: 'Text', multiline: true },
        { path: ['image'], label: 'Bild', media: true },
      ],
    },
  ],
  reviews: [
    {
      path: ['items'],
      label: 'Reviews',
      addLabel: 'Review',
      emptyItem: { name: 'Neuer Kunde', city: '', text: '' },
      fields: [
        { path: ['name'], label: 'Name' },
        { path: ['city'], label: 'Ort' },
        { path: ['text'], label: 'Text', multiline: true },
      ],
    },
  ],
  faq: [
    {
      path: ['items'],
      label: 'FAQ-Fragen',
      addLabel: 'Frage',
      emptyItem: { q: 'Neue Frage', a: '' },
      fields: [
        { path: ['q'], label: 'Frage' },
        { path: ['a'], label: 'Antwort', multiline: true },
      ],
    },
  ],
};

const trustbarLinesToText = (value) => Array.isArray(value) ? value.join('\n') : '';
const textToTrustbarLines = (text) => String(text || '')
  .split(/\r?\n/)
  .map((line) => line.trim())
  .filter(Boolean);

function BlueprintLinesTextarea({ value, onChange, rows, className, placeholder, helperText }) {
  const externalText = trustbarLinesToText(value);
  const [draft, setDraft] = useState(externalText);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(externalText);
  }, [externalText, focused]);

  const update = (nextText) => {
    setDraft(nextText);
    const lines = textToTrustbarLines(nextText);
    onChange(lines.length > 0 ? lines : undefined);
  };

  return (
    <textarea
      value={focused ? draft : externalText}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setDraft(textToTrustbarLines(draft).join('\n'));
      }}
      onChange={(e) => update(e.target.value)}
      placeholder={placeholder || 'Eine Zeile pro Eintrag'}
      rows={rows || 5}
      className={className}
    />
  );
}

function BlueprintDraftField({ value, onChange, multiline = false, rows = 3, placeholder = '', className = '' }) {
  const externalText = value ?? '';
  const [draft, setDraft] = useState(externalText);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(externalText);
  }, [externalText, focused]);

  const update = (nextText) => {
    setDraft(nextText);
    onChange(nextText);
  };

  const shared = {
    value: focused ? draft : externalText,
    onFocus: () => setFocused(true),
    onBlur: () => {
      setFocused(false);
      onChange(draft);
    },
    onChange: (e) => update(e.target.value),
    placeholder,
    className,
  };

  return multiline ? (
    <textarea {...shared} rows={rows} />
  ) : (
    <input {...shared} />
  );
}

function BlueprintOverrideField({ pageIndex, selected, sectionType, lang, field, updatePage, overlay, onChange, mediaItems, mediaLoading }) {
  const localPath = ['sectionOverrides', sectionType, lang, ...field.path];
  const value = getAt(selected, localPath);
  const overridden = value !== undefined;
  const reset = () => updatePage(localPath, undefined);
  if (field.media) {
    return (
      <div>
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">{field.label}</span>
        <MediaUrlField
          path={['pages', pageIndex, ...localPath]}
          base=""
          overlay={overlay}
          onChange={onChange}
          testId={`blueprint-${sectionType}-${field.path.join('-')}`}
          mediaItems={mediaItems}
          mediaLoading={mediaLoading}
        />
      </div>
    );
  }
  const cls = `w-full px-3 py-2 rounded-lg border-2 ${overridden ? 'border-[#F26B1A] bg-[#FFF8F0]' : 'border-[#E5E0D2]'} focus:border-[#F26B1A] focus:outline-none text-[13px]`;
  return (
    <label className={`block ${field.fullWidth ? 'sm:col-span-2' : ''}`}>
      <span className="flex items-center justify-between gap-2 text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">
        {field.label}
        {overridden && (
          <button type="button" onClick={reset} className="normal-case tracking-normal text-[10.5px] text-[#F26B1A] hover:text-[#D85A0A]">
            Reset
          </button>
        )}
      </span>
      {field.lines ? (
        <>
          <BlueprintLinesTextarea
            value={value}
            onChange={(lines) => updatePage(localPath, lines)}
            rows={field.rows}
            placeholder={'Eine Zeile pro Trustbar-Text\nz.B. Ohne Bohren moeglich\nz.B. Maatwerk voor elk kozijn'}
            className={`${cls} resize-y font-mono leading-relaxed`}
          />
          <span className="block mt-1 text-[10.5px] text-[#5C5852]">
            Eine Zeile wird ein laufender Trustbar-Satz.
          </span>
        </>
      ) : field.multiline ? (
        <BlueprintDraftField
          value={value}
          onChange={(nextValue) => updatePage(localPath, nextValue)}
          multiline
          placeholder="Leer lassen = Startseitenwert verwenden"
          rows={3}
          className={`${cls} resize-none`}
        />
      ) : (
        <BlueprintDraftField
          value={value}
          onChange={(nextValue) => updatePage(localPath, nextValue)}
          placeholder="Leer lassen = Startseitenwert verwenden"
          className={cls}
        />
      )}
    </label>
  );
}

function getBlueprintRepeaterFallback(sectionType, lang, repeater) {
  const section = BASE_TRANSLATIONS?.[lang]?.[sectionType] || {};
  const direct = getAt(section, repeater.path);
  const legacy = repeater.legacyPath ? getAt(section, repeater.legacyPath) : undefined;
  const list = Array.isArray(direct) ? direct : Array.isArray(legacy) ? legacy : [];
  return deepClone(list);
}

function BlueprintRepeaterField({ pageIndex, sectionType, lang, repeater, selected, updatePage, overlay, onChange, mediaItems, mediaLoading }) {
  const localPath = ['sectionOverrides', sectionType, lang, ...repeater.path];
  const raw = getAt(selected, localPath);
  const fallbackList = useMemo(() => getBlueprintRepeaterFallback(sectionType, lang, repeater), [sectionType, lang, repeater]);
  const overridden = Array.isArray(raw);
  const list = overridden ? raw : fallbackList;
  const updateList = (next) => updatePage(localPath, next);
  const copyDefaults = () => updateList(fallbackList.length ? fallbackList : [deepClone(repeater.emptyItem || {})]);
  const addItem = () => updateList([...(overridden ? list : fallbackList), deepClone(repeater.emptyItem || {})]);
  const removeItem = (idx) => updateList(list.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    const [item] = next.splice(idx, 1);
    next.splice(target, 0, item);
    updateList(next);
  };
  const updateItem = (idx, path, value) => {
    const source = overridden ? list : fallbackList;
    const next = deepClone(source);
    next[idx] = setAt(next[idx] || {}, path, value);
    updateList(next);
  };
  const resetList = () => updatePage(localPath, undefined);

  return (
    <div className="sm:col-span-2 rounded-xl border border-[#E5E0D2] bg-[#FAF8F2] p-3 lg:p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[12px] uppercase tracking-[0.16em] font-semibold text-[#5C5852]">{repeater.label}</p>
          <p className="text-[11px] text-[#5C5852] mt-1">
            {overridden ? 'Diese Liste ist fuer diese Seite individuell ueberschrieben.' : 'Aktuell werden Startseitenwerte verwendet.'}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {!overridden && fallbackList.length > 0 && (
            <button type="button" onClick={copyDefaults} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white text-[#5C5852] text-[11px] font-semibold hover:text-[#F26B1A]">
              <RefreshCcw className="w-3.5 h-3.5" /> Startwerte uebernehmen
            </button>
          )}
          {overridden && (
            <button type="button" onClick={resetList} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white text-[#F26B1A] text-[11px] font-semibold hover:text-[#D85A0A]">
              Reset
            </button>
          )}
          <button type="button" onClick={addItem} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F26B1A] text-white text-[11px] font-semibold hover:bg-[#D85A0A]">
            <Plus className="w-3.5 h-3.5" /> {repeater.addLabel || 'Eintrag'}
          </button>
        </div>
      </div>

      {!overridden && fallbackList.length > 0 && (
        <p className="rounded-lg bg-white border border-[#E5E0D2] p-3 text-[12px] text-[#5C5852]">
          Zum Bearbeiten einzelner Bilder oder Texte zuerst die Startwerte fuer diese Seite uebernehmen. Danach bleibt jede Aenderung nur auf dieser Unterseite.
        </p>
      )}

      {list.length === 0 ? (
        <p className="rounded-lg bg-white border border-[#E5E0D2] p-4 text-center text-[12.5px] text-[#5C5852]">Noch keine Eintraege.</p>
      ) : (
        <div className="space-y-3">
          {list.map((item, idx) => {
            const itemTitle = item?.title || item?.name || item?.t || item?.q || `${repeater.addLabel || 'Eintrag'} ${idx + 1}`;
            return (
              <div key={`${repeater.label}-${idx}`} className="rounded-xl border border-[#E5E0D2] bg-white p-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-[13px] text-[#1F1D1A] truncate">{idx + 1}. {itemTitle}</p>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => moveItem(idx, -1)} disabled={!overridden || idx === 0} className="p-1 rounded hover:bg-[#FAF8F2] disabled:opacity-25">
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moveItem(idx, 1)} disabled={!overridden || idx === list.length - 1} className="p-1 rounded hover:bg-[#FAF8F2] disabled:opacity-25">
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => removeItem(idx)} disabled={!overridden} className="p-1 rounded text-[#C03A1A] hover:bg-[#C03A1A]/10 disabled:opacity-25">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {repeater.fields.map((field) => {
                    const fieldValue = getAt(item, field.path);
                    const pathLabel = field.path.join('-');
                    if (field.media) {
                      return (
                        <div key={pathLabel} className="lg:col-span-2">
                          <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">{field.label}</span>
                          {overridden ? (
                            <MediaUrlField
                              path={['pages', pageIndex, ...localPath, idx, ...field.path]}
                              base=""
                              overlay={overlay}
                              onChange={onChange}
                              testId={`blueprint-${sectionType}-${repeater.path.join('-')}-${idx}-${pathLabel}`}
                              mediaItems={mediaItems}
                              mediaLoading={mediaLoading}
                            />
                          ) : (
                            <p className="rounded-lg border border-[#E5E0D2] bg-[#FAF8F2] p-3 text-[12px] text-[#5C5852]">
                              Startwerte uebernehmen, um dieses Bild individuell zu aendern.
                            </p>
                          )}
                        </div>
                      );
                    }
                    const cls = "w-full px-3 py-2 rounded-lg border border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[13px]";
                    return (
                      <label key={pathLabel} className={`block ${field.multiline || field.lines ? 'lg:col-span-2' : ''}`}>
                        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">{field.label}</span>
                        {field.lines ? (
                          <BlueprintLinesTextarea
                            value={fieldValue}
                            onChange={(lines) => updateItem(idx, field.path, lines)}
                            rows={4}
                            placeholder="Eine Zeile pro Punkt"
                            className={`${cls} resize-y font-mono leading-relaxed`}
                          />
                        ) : field.multiline ? (
                          <BlueprintDraftField
                            value={fieldValue}
                            onChange={(nextValue) => updateItem(idx, field.path, nextValue)}
                            multiline
                            rows={3}
                            className={`${cls} resize-y`}
                          />
                        ) : (
                          <BlueprintDraftField
                            value={fieldValue}
                            onChange={(nextValue) => updateItem(idx, field.path, nextValue)}
                            className={cls}
                          />
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BlueprintSectionsEditor({ selected, pages, lang, overlay, onChange, updatePage, mediaItems, mediaLoading, detailsTop = null, seoPanel = null, splitColumns = false }) {
  const pageIndex = pages.findIndex((p) => p.id === selected.id);
  const sections = normalizeBlueprintSections(selected.sections);
  const [openType, setOpenType] = useState(sections[0]?.type || 'hero');

  const writeSections = (nextSections) => updatePage(['sections'], nextSections);
  const toggleSection = (type, enabled) => {
    writeSections(sections.map((section) => section.type === type ? { ...section, enabled } : section));
  };
  const moveSection = (index, dir) => {
    const next = [...sections];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    writeSections(next);
  };
  const resetSections = () => writeSections(createHomeBlueprintSections());
  const selectedSection = sections.find((section) => section.type === openType) || sections[0];
  const selectedIndex = selectedSection ? sections.findIndex((section) => section.type === selectedSection.type) : -1;
  const selectedFields = selectedSection ? (BLUEPRINT_SECTION_FIELDS[selectedSection.type] || []) : [];
  const selectedRepeaters = selectedSection ? (BLUEPRINT_SECTION_REPEATERS[selectedSection.type] || []) : [];

  const sectionPicker = (
    <aside className={`${splitColumns ? 'bg-white rounded-2xl border border-[#E5E0D2] xl:h-full min-h-0 flex flex-col overflow-hidden' : '2xl:col-span-3 rounded-xl border border-[#E5E0D2] bg-[#FAF8F2] overflow-hidden'}`}>
      <div className={`${splitColumns ? 'p-4' : 'p-3'} border-b border-[#E5E0D2] bg-white shrink-0`}>
        <div>
          <p className="font-display text-[18px] text-[#1F1D1A]">Startseiten-Layout</p>
          <p className="text-[11.5px] text-[#5C5852]">Abschnitte waehlen und schnell wechseln.</p>
        </div>
        <button type="button" onClick={resetSections} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF8F2] text-[#5C5852] text-[12px] font-semibold hover:text-[#F26B1A]">
          <RefreshCcw className="w-3.5 h-3.5" /> Reihenfolge resetten
        </button>
      </div>
      <div className={`${splitColumns ? 'flex-1 min-h-0' : 'max-h-[560px]'} p-2 space-y-1 overflow-y-auto`}>
        {sections.map((section, index) => {
          const active = selectedSection?.type === section.type;
          return (
            <div key={section.id} className={`rounded-xl px-2 py-2 ${active ? 'bg-[#F26B1A] text-white' : 'bg-white text-[#1F1D1A]'}`}>
              <button type="button" onClick={() => setOpenType(section.type)} className="w-full text-left flex items-center gap-2">
                <PanelTop className="w-4 h-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-semibold truncate">{getBlueprintSectionLabel(section.type)}</span>
                  <span className={`block text-[10px] font-mono truncate ${active ? 'text-white/70' : 'text-[#5C5852]'}`}>{index + 1}. {section.type}</span>
                </span>
                <span className={`w-2 h-2 rounded-full ${section.enabled === false ? 'bg-[#C03A1A]' : 'bg-[#7FC242]'}`} />
              </button>
              {active && (
                <div className="mt-2 flex items-center gap-1.5 pl-6">
                  <label className="inline-flex items-center gap-1 text-[11px] text-white/80">
                    <input type="checkbox" checked={section.enabled !== false} onChange={(e) => toggleSection(section.type, e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#1F1D1A]" />
                    aktiv
                  </label>
                  <button type="button" onClick={() => moveSection(index, -1)} disabled={index === 0} className="p-1 rounded hover:bg-white/10 disabled:opacity-25">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => moveSection(index, 1)} disabled={index === sections.length - 1} className="p-1 rounded hover:bg-white/10 disabled:opacity-25">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );

  const sectionEditor = (
    <section className={`${splitColumns ? 'bg-white rounded-2xl border border-[#E5E0D2] p-5 xl:h-full min-h-0 xl:overflow-y-auto overscroll-contain' : '2xl:col-span-9 rounded-xl border border-[#E5E0D2] bg-white p-4'}`}>
      <div className="space-y-5">
        {detailsTop}
        <div className={`${detailsTop ? 'border-t border-[#E5E0D2] pt-5' : ''}`}>
          {!selectedSection ? (
            <p className="p-10 text-center text-[14px] text-[#5C5852]">Keine Abschnitte vorhanden.</p>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="font-display text-[20px] text-[#1F1D1A]">{getBlueprintSectionLabel(selectedSection.type)}</p>
                <p className="text-[11.5px] text-[#5C5852] font-mono">{selectedIndex + 1}. {selectedSection.type}</p>
              </div>
              {selectedFields.length === 0 && selectedRepeaters.length === 0 ? (
                <p className="text-[12.5px] text-[#5C5852] rounded-xl bg-[#FAF8F2] p-4">
                  Diese Sektion nutzt aktuell die Startseiten-Daten ohne eigene Textfelder.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {selectedFields.map((field) => (
                    <BlueprintOverrideField
                      key={`${selectedSection.type}-${field.path.join('.')}`}
                      pageIndex={pageIndex}
                      selected={selected}
                      sectionType={selectedSection.type}
                      lang={lang}
                      field={field}
                      updatePage={updatePage}
                      overlay={overlay}
                      onChange={onChange}
                      mediaItems={mediaItems}
                      mediaLoading={mediaLoading}
                    />
                  ))}
                  {selectedRepeaters.map((repeater) => (
                    <BlueprintRepeaterField
                      key={`${selectedSection.type}-${repeater.path.join('.')}`}
                      pageIndex={pageIndex}
                      selected={selected}
                      sectionType={selectedSection.type}
                      lang={lang}
                      repeater={repeater}
                      updatePage={updatePage}
                      overlay={overlay}
                      onChange={onChange}
                      mediaItems={mediaItems}
                      mediaLoading={mediaLoading}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {seoPanel}
      </div>
    </section>
  );

  if (splitColumns) {
    return (
      <>
        {sectionPicker}
        {sectionEditor}
      </>
    );
  }

  return (
    <div className="border-t border-[#E5E0D2] pt-5 space-y-4">
      <div className="grid grid-cols-1 2xl:grid-cols-12 gap-4">
        {sectionPicker}
        {sectionEditor}
      </div>

      <div className="rounded-xl bg-[#1F1D1A] text-white p-4">
        <p className="text-[13px] font-semibold">Workflow fuer alte Megamenue-Seiten</p>
        <p className="text-[12px] text-white/70 mt-1">
          Ich kann daraus jetzt Seiten wie Fenster, Tueren, Plisseetueren oder Lichtschachtabdeckungen erzeugen: Startseitenstruktur verwenden, DE schreiben, NL menschlich formulieren, SEO setzen, Medien zuweisen.
        </p>
      </div>
    </div>
  );
}

const BLOCK_ICONS = {
  hero: ImageIcon,
  text: FileText,
  image_text: PanelTop,
  image: ImageIcon,
  cta: Tags,
  faq: Globe,
};

function SmallTextField({ label, value, onChange, multiline = false, placeholder = '' }) {
  const cls = "w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]";
  return (
    <label className="block">
      <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">{label}</span>
      {multiline ? (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder} className={`${cls} resize-y`} />
      ) : (
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} />
      )}
    </label>
  );
}

function FaqItemsEditor({ items = [], onChange }) {
  const list = Array.isArray(items) ? items : [];
  const update = (idx, key, value) => {
    const next = [...list];
    next[idx] = { ...(next[idx] || {}), [key]: value };
    onChange(next);
  };
  const add = () => onChange([...list, { q: 'Neue Frage', a: 'Antwort eintragen.' }]);
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852]">FAQ Eintraege</span>
        <button type="button" onClick={add} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FAF8F2] text-[#5C5852] text-[11px] font-semibold hover:text-[#F26B1A]">
          <Plus className="w-3.5 h-3.5" /> Frage
        </button>
      </div>
      {list.map((item, idx) => (
        <div key={idx} className="rounded-xl border border-[#E5E0D2] bg-[#FAF8F2] p-3 space-y-2">
          <div className="flex justify-between gap-2">
            <p className="text-[12px] font-semibold text-[#1F1D1A]">Frage {idx + 1}</p>
            <button type="button" onClick={() => remove(idx)} className="text-[#C03A1A]"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          <input value={item.q || ''} onChange={(e) => update(idx, 'q', e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-[#E5E0D2] text-[13px] focus:outline-none focus:border-[#F26B1A]" />
          <textarea value={item.a || ''} onChange={(e) => update(idx, 'a', e.target.value)}
            rows={2} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D2] text-[13px] focus:outline-none focus:border-[#F26B1A] resize-y" />
        </div>
      ))}
    </div>
  );
}

function PageBlockFields({ block, blockIndex, pageIndex, lang, updateBlock, overlay, onChange, mediaItems, mediaLoading }) {
  const trPath = ['translations', lang];
  const tr = block.translations?.[lang] || block.translations?.nl || {};
  const setT = (key, value) => updateBlock(block.id, [...trPath, key], value);
  const mediaField = (key, label) => (
    <div>
      <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">{label}</span>
      <MediaUrlField
        path={['pages', pageIndex, 'blocks', blockIndex, 'translations', lang, key]}
        base=""
        overlay={overlay}
        onChange={onChange}
        testId={`page-block-${block.id}-${key}`}
        mediaItems={mediaItems}
        mediaLoading={mediaLoading}
      />
    </div>
  );

  if (block.type === 'hero') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SmallTextField label="Eyebrow" value={tr.eyebrow} onChange={(v) => setT('eyebrow', v)} />
        <SmallTextField label="Button Text" value={tr.button_text} onChange={(v) => setT('button_text', v)} />
        <SmallTextField label="Titel" value={tr.title} onChange={(v) => setT('title', v)} />
        <SmallTextField label="Button Link" value={tr.button_link} onChange={(v) => setT('button_link', v)} />
        <SmallTextField label="Subline" value={tr.subtitle} onChange={(v) => setT('subtitle', v)} multiline />
        {mediaField('image', 'Hintergrundbild')}
      </div>
    );
  }

  if (block.type === 'text') {
    return (
      <div className="space-y-4">
        <SmallTextField label="Titel" value={tr.title} onChange={(v) => setT('title', v)} />
        <div>
          <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Text</span>
          <RichHtmlEditor value={tr.html || ''} onChange={(html) => setT('html', html)} testId={`block-html-${block.id}`} />
        </div>
      </div>
    );
  }

  if (block.type === 'image_text') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SmallTextField label="Eyebrow" value={tr.eyebrow} onChange={(v) => setT('eyebrow', v)} />
          <label className="block">
            <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Anordnung</span>
            <select value={block.layout || 'media-right'} onChange={(e) => updateBlock(block.id, ['layout'], e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] bg-white">
              <option value="media-right">Text links, Bild rechts</option>
              <option value="media-left">Bild links, Text rechts</option>
            </select>
          </label>
          <SmallTextField label="Titel" value={tr.title} onChange={(v) => setT('title', v)} />
          {mediaField('image', 'Bild')}
        </div>
        <div>
          <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Text</span>
          <RichHtmlEditor value={tr.html || ''} onChange={(html) => setT('html', html)} testId={`block-image-text-${block.id}`} />
        </div>
      </div>
    );
  }

  if (block.type === 'image') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SmallTextField label="Titel" value={tr.title} onChange={(v) => setT('title', v)} />
        <SmallTextField label="Alt/Label" value={tr.alt} onChange={(v) => setT('alt', v)} />
        {mediaField('image', 'Bild oder PDF')}
        <SmallTextField label="Bildunterschrift" value={tr.caption} onChange={(v) => setT('caption', v)} multiline />
      </div>
    );
  }

  if (block.type === 'cta') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SmallTextField label="Eyebrow" value={tr.eyebrow} onChange={(v) => setT('eyebrow', v)} />
        <SmallTextField label="Button Text" value={tr.button_text} onChange={(v) => setT('button_text', v)} />
        <SmallTextField label="Titel" value={tr.title} onChange={(v) => setT('title', v)} />
        <SmallTextField label="Button Link" value={tr.button_link} onChange={(v) => setT('button_link', v)} />
        <SmallTextField label="Text" value={tr.text} onChange={(v) => setT('text', v)} multiline />
      </div>
    );
  }

  if (block.type === 'faq') {
    return (
      <div className="space-y-4">
        <SmallTextField label="Titel" value={tr.title} onChange={(v) => setT('title', v)} />
        <FaqItemsEditor items={tr.items} onChange={(items) => setT('items', items)} />
      </div>
    );
  }

  return null;
}

function PageBlocksEditor({ selected, pages, lang, overlay, onChange, updatePage, mediaItems, mediaLoading }) {
  const pageIndex = pages.findIndex((p) => p.id === selected.id);
  const blocks = normalizePageBlocks(selected.blocks);
  const [selectedBlockId, setSelectedBlockId] = useState(blocks[0]?.id || null);
  const selectedBlock = blocks.find((block) => block.id === selectedBlockId) || blocks[0] || null;
  const blockIndex = selectedBlock ? blocks.findIndex((block) => block.id === selectedBlock.id) : -1;

  useEffect(() => {
    if (!selectedBlockId || !blocks.some((block) => block.id === selectedBlockId)) {
      setSelectedBlockId(blocks[0]?.id || null);
    }
  }, [blocks, selectedBlockId]);

  useEffect(() => {
    if (!Array.isArray(selected.blocks) || selected.blocks.length === 0) {
      updatePage(['blocks'], blocks);
    }
  }, [selected.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const writeBlocks = (next) => updatePage(['blocks'], next);
  const addBlock = (type) => {
    const block = createPageBlock(type);
    writeBlocks([...blocks, block]);
    setSelectedBlockId(block.id);
  };
  const updateBlock = (id, path, value) => {
    writeBlocks(blocks.map((block) => block.id === id ? setAt(block, path, value) : block));
  };
  const deleteBlock = (id) => {
    if (!window.confirm('Diesen Abschnitt wirklich loeschen?')) return;
    const next = blocks.filter((block) => block.id !== id);
    writeBlocks(next);
    setSelectedBlockId(next[0]?.id || null);
  };
  const moveBlock = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    const [item] = next.splice(idx, 1);
    next.splice(target, 0, item);
    writeBlocks(next);
  };

  return (
    <div className="border-t border-[#E5E0D2] pt-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <p className="font-display text-[18px] text-[#1F1D1A]">Seiten-Layout</p>
          <p className="text-[11.5px] text-[#5C5852]">Abschnitte links waehlen, rechts direkt bearbeiten. Texte sind je Sprache getrennt.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-12 gap-4">
        <aside className="2xl:col-span-3 rounded-xl border border-[#E5E0D2] bg-[#FAF8F2] overflow-hidden">
          <div className="p-3 border-b border-[#E5E0D2] bg-white">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold mb-2">Block hinzufuegen</p>
            <div className="grid grid-cols-2 gap-1.5">
              {PAGE_BLOCK_TYPES.map((type) => {
                const Icon = BLOCK_ICONS[type.id] || FileText;
                return (
                  <button key={type.id} type="button" onClick={() => addBlock(type.id)}
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-[#FAF8F2] text-[#1F1D1A] text-[11.5px] font-semibold hover:bg-[#F26B1A] hover:text-white">
                    <Icon className="w-3.5 h-3.5" /> {type.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-2 space-y-1 max-h-[560px] overflow-y-auto">
            {blocks.map((block, idx) => {
              const Icon = BLOCK_ICONS[block.type] || FileText;
              const label = block.translations?.[lang]?.title || block.translations?.nl?.title || getPageBlockLabel(block.type);
              const active = selectedBlock?.id === block.id;
              return (
                <button key={block.id} type="button" onClick={() => setSelectedBlockId(block.id)}
                  className={`w-full text-left rounded-xl px-3 py-2.5 transition-colors ${active ? 'bg-[#F26B1A] text-white' : 'bg-white hover:bg-[#FFF8F0] text-[#1F1D1A]'}`}>
                  <span className="flex items-center gap-2">
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[12.5px] font-semibold truncate">{label}</span>
                      <span className={`block text-[10px] font-mono truncate ${active ? 'text-white/70' : 'text-[#5C5852]'}`}>{idx + 1}. {block.type}</span>
                    </span>
                    <span className={`w-2 h-2 rounded-full ${block.enabled === false ? 'bg-[#C03A1A]' : 'bg-[#7FC242]'}`} />
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="2xl:col-span-9 rounded-xl border border-[#E5E0D2] bg-white p-4">
          {!selectedBlock ? (
            <p className="p-10 text-center text-[14px] text-[#5C5852]">Lege den ersten Abschnitt an.</p>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-display text-[20px] text-[#1F1D1A]">{getPageBlockLabel(selectedBlock.type)}</p>
                  <p className="text-[11.5px] text-[#5C5852] font-mono">{selectedBlock.id}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <label className="inline-flex items-center gap-1.5 text-[12px] text-[#5C5852] px-2">
                    <input type="checkbox" checked={selectedBlock.enabled !== false} onChange={(e) => updateBlock(selectedBlock.id, ['enabled'], e.target.checked)}
                      className="w-4 h-4 accent-[#F26B1A]" />
                    aktiv
                  </label>
                  <button type="button" onClick={() => moveBlock(blockIndex, -1)} disabled={blockIndex <= 0} className="p-2 rounded-full hover:bg-[#FAF8F2] disabled:opacity-25">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => moveBlock(blockIndex, 1)} disabled={blockIndex >= blocks.length - 1} className="p-2 rounded-full hover:bg-[#FAF8F2] disabled:opacity-25">
                    <ArrowDown className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => deleteBlock(selectedBlock.id)} className="p-2 rounded-full text-[#C03A1A] hover:bg-[#C03A1A]/10">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <PageBlockFields
                block={selectedBlock}
                blockIndex={blockIndex}
                pageIndex={pageIndex}
                lang={lang}
                updateBlock={updateBlock}
                overlay={overlay}
                onChange={onChange}
                mediaItems={mediaItems}
                mediaLoading={mediaLoading}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function emptyPage(template = 'simple') {
  if (template === LAYOUT_EDITOR_TEMPLATE) return createLayoutEditorPage();
  if (template === HOME_BLUEPRINT_TEMPLATE) return createHomeBlueprintPage();
  const id = `page-${Date.now()}`;
  return {
    id,
    path: '/neue-seite',
    template: 'simple',
    published: false,
    image: '',
    translations: {
      nl: { title: 'Nieuwe pagina', eyebrow: 'Informatie', excerpt: '', body: '' },
      de: { title: 'Neue Seite', eyebrow: 'Information', excerpt: '', body: '' },
    },
    seo: {
      nl: { title: '', description: '', image: '', noindex: false },
      de: { title: '', description: '', image: '', noindex: false },
    },
  };
}

function PagesTab({ overlay, onChange }) {
  const pages = Array.isArray(overlay.pages) ? overlay.pages : [];
  const [selectedId, setSelectedId] = useState(pages[0]?.id || null);
  const [lang, setLang] = useState('nl');
  const { items, loading } = useMediaLibrary();
  const selected = pages.find((p) => p.id === selectedId) || pages[0] || null;

  const writePages = (nextPagesOrUpdater) => {
    onChange((prev) => {
      const base = prev || overlay || {};
      const currentPages = Array.isArray(base.pages) ? base.pages : [];
      const nextPages = typeof nextPagesOrUpdater === 'function'
        ? nextPagesOrUpdater(currentPages)
        : nextPagesOrUpdater;
      return { ...base, pages: nextPages };
    });
  };
  const addPage = (template = LAYOUT_EDITOR_TEMPLATE) => {
    const page = emptyPage(template);
    writePages((currentPages) => [...currentPages, page]);
    setSelectedId(page.id);
  };
  const deletePage = (id) => {
    if (!window.confirm('Diese Unterseite wirklich loeschen?')) return;
    const next = pages.filter((p) => p.id !== id);
    writePages(next);
    setSelectedId(next[0]?.id || null);
  };
  const updatePage = (path, value) => {
    if (!selected?.id) return;
    const targetId = selected.id;
    writePages((currentPages) => currentPages.map((p) => p.id === targetId ? setAt(p, path, value) : p));
  };
  const setPageTemplate = (template) => {
    if (!selected?.id) return;
    const targetId = selected.id;
    writePages((currentPages) => currentPages.map((p) => {
      if (p.id !== targetId) return p;
      if (template === HOME_BLUEPRINT_TEMPLATE) {
        const seed = createHomeBlueprintPage({ id: p.id, path: p.path });
        return {
          ...p,
          template,
          sections: normalizeBlueprintSections(p.sections || seed.sections),
          sectionOverrides: p.sectionOverrides || seed.sectionOverrides,
        };
      }
      if (template === LAYOUT_EDITOR_TEMPLATE) {
        const seed = createLayoutEditorPage({ id: p.id, path: p.path, translations: p.translations, seo: p.seo, blocks: p.blocks });
        return {
          ...p,
          template,
          blocks: normalizePageBlocks(p.blocks || seed.blocks),
        };
      }
      return { ...p, template: 'simple' };
    }));
  };
  const isHomeBlueprint = selected?.template === HOME_BLUEPRINT_TEMPLATE;
  const selectedPageIndex = selected ? pages.findIndex((p) => p.id === selected.id) : -1;
  const pageDetails = selected ? (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-display text-[20px] text-[#1F1D1A]">{selected.translations?.[lang]?.title || selected.path}</p>
          <p className="text-[11.5px] text-[#5C5852]">URL, Inhalt, Bild und SEO dieser Unterseite.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={selected.path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF8F2] text-[#5C5852] text-[12px] font-semibold hover:text-[#F26B1A]">
            <ExternalLink className="w-3.5 h-3.5" /> Vorschau
          </a>
          <button type="button" onClick={() => deletePage(selected.id)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C03A1A]/10 text-[#C03A1A] text-[12px] font-semibold">
            <Trash2 className="w-3.5 h-3.5" /> Loeschen
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Pfad</span>
          <input value={selected.path || ''} onChange={(e) => updatePage(['path'], slugifyPath(e.target.value))}
            className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] font-mono" />
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
          <label className="block">
            <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Vorlage</span>
            <select value={selected.template || 'simple'} onChange={(e) => setPageTemplate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] bg-white">
              <option value="simple">Einfache Seite</option>
              <option value={LAYOUT_EDITOR_TEMPLATE}>Layout-Editor</option>
              <option value={HOME_BLUEPRINT_TEMPLATE}>Startseiten-Vorlage</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-[13px] text-[#1F1D1A] pb-3">
            <input type="checkbox" checked={selected.published !== false} onChange={(e) => updatePage(['published'], e.target.checked)}
              className="w-4 h-4 accent-[#F26B1A]" />
            Veröffentlicht
          </label>
        </div>
      </div>

      <div className="bg-[#FAF8F2] rounded-xl p-1 inline-flex gap-1">
        {['nl', 'de'].map((l) => (
          <button key={l} type="button" onClick={() => setLang(l)}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold ${lang === l ? 'bg-[#F26B1A] text-white' : 'text-[#5C5852]'}`}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      <label className="block">
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Titel</span>
        <input value={selected.translations?.[lang]?.title || ''} onChange={(e) => updatePage(['translations', lang, 'title'], e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
      </label>
      <label className="block">
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Kurzbeschreibung</span>
        <textarea value={selected.translations?.[lang]?.excerpt || ''} onChange={(e) => updatePage(['translations', lang, 'excerpt'], e.target.value)}
          rows={2} className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] resize-none" />
      </label>
    </>
  ) : null;
  const seoPanel = selected ? (
    <div className="border-t border-[#E5E0D2] pt-5 space-y-4">
      <p className="font-display text-[17px] text-[#1F1D1A]">SEO fuer diese Unterseite</p>
      <label className="block">
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">SEO Title</span>
        <input value={selected.seo?.[lang]?.title || ''} onChange={(e) => updatePage(['seo', lang, 'title'], e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
      </label>
      <label className="block">
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Meta Description</span>
        <textarea value={selected.seo?.[lang]?.description || ''} onChange={(e) => updatePage(['seo', lang, 'description'], e.target.value)}
          rows={2} className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] resize-none" />
      </label>
      <label className="inline-flex items-center gap-2 text-[13px] text-[#1F1D1A]">
        <input type="checkbox" checked={selected.seo?.[lang]?.noindex === true} onChange={(e) => updatePage(['seo', lang, 'noindex'], e.target.checked)}
          className="w-4 h-4 accent-[#F26B1A]" />
        noindex
      </label>
    </div>
  ) : null;

  return (
    <div data-testid="content-tab-pages" className={`grid grid-cols-1 gap-4 min-h-0 xl:h-full xl:overflow-hidden ${isHomeBlueprint ? 'xl:grid-cols-[minmax(320px,440px)_minmax(300px,360px)_minmax(0,1fr)]' : 'xl:grid-cols-12'}`}>
      <aside className={`${isHomeBlueprint ? '' : 'xl:col-span-3'} bg-white rounded-2xl border border-[#E5E0D2] overflow-hidden xl:h-full min-h-0 flex flex-col`}>
        <div className="p-3 border-b border-[#E5E0D2] flex items-center justify-between gap-2 shrink-0">
          <div>
            <p className="font-display text-[17px] text-[#1F1D1A]">Seitenbrowser</p>
            <p className="text-[11.5px] text-[#5C5852] mt-1">Eigene Unterseiten.</p>
          </div>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => addPage(LAYOUT_EDITOR_TEMPLATE)} title="Layout-Editor"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#FAF8F2] text-[#5C5852] hover:text-[#F26B1A]">
              <Plus className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => addPage(HOME_BLUEPRINT_TEMPLATE)} title="Startseiten-Vorlage"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F26B1A] text-white hover:bg-[#D85A0A]">
              <LayoutTemplate className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-2 space-y-1 flex-1 min-h-0 overflow-y-auto">
          {pages.length === 0 ? (
            <p className="p-4 text-[12.5px] text-[#5C5852]">Noch keine eigenen Unterseiten.</p>
          ) : pages.map((page) => {
            const label = page.translations?.nl?.title || page.path || 'Unbenannte Seite';
            return (
              <button key={page.id} type="button" onClick={() => setSelectedId(page.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-[12.5px] transition-colors ${selected?.id === page.id ? 'bg-[#F26B1A] text-white' : 'hover:bg-[#FAF8F2] text-[#1F1D1A]'}`}>
                <span className="flex items-center justify-between gap-2">
                  <span className="font-semibold truncate">{label}</span>
                  <span className="inline-flex items-center gap-1 shrink-0">
                    {page.template === HOME_BLUEPRINT_TEMPLATE && <LayoutTemplate className="w-3 h-3 opacity-80" />}
                    {page.template === LAYOUT_EDITOR_TEMPLATE && <PanelTop className="w-3 h-3 opacity-80" />}
                    <span className={`w-2 h-2 rounded-full ${page.published === false ? 'bg-[#C03A1A]' : 'bg-[#7FC242]'}`} />
                  </span>
                </span>
                <span className={`block font-mono text-[10.5px] truncate ${selected?.id === page.id ? 'text-white/70' : 'text-[#5C5852]'}`}>{page.path}</span>
              </button>
            );
          })}
        </div>
      </aside>

      <section className={`${isHomeBlueprint ? 'xl:col-span-2 xl:h-full min-h-0 xl:overflow-hidden' : 'xl:col-span-9 bg-white rounded-2xl border border-[#E5E0D2] p-5 xl:h-full min-h-0 xl:overflow-y-auto overscroll-contain'}`}>
        {!selected ? (
          <div className="p-10 text-center">
            <p className="text-[14px] text-[#5C5852] mb-4">Lege deine erste Unterseite an.</p>
            <div className="inline-flex items-center gap-2">
              <button type="button" onClick={() => addPage(LAYOUT_EDITOR_TEMPLATE)} className="pill pill-leaf"><Plus className="w-4 h-4" /> Neue Layout-Seite</button>
              <button type="button" onClick={() => addPage(HOME_BLUEPRINT_TEMPLATE)} className="pill pill-leaf"><LayoutTemplate className="w-4 h-4" /> Aus Startseite</button>
            </div>
          </div>
        ) : isHomeBlueprint ? (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(300px,360px)_minmax(0,1fr)] gap-5 h-full min-h-0">
            <BlueprintSectionsEditor
              selected={selected}
              pages={pages}
              lang={lang}
              overlay={overlay}
              onChange={onChange}
              updatePage={updatePage}
              mediaItems={items}
              mediaLoading={loading}
              detailsTop={pageDetails}
              seoPanel={seoPanel}
              splitColumns
            />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-display text-[20px] text-[#1F1D1A]">{selected.translations?.[lang]?.title || selected.path}</p>
                <p className="text-[11.5px] text-[#5C5852]">URL, Inhalt, Bild und SEO dieser Unterseite.</p>
              </div>
              <div className="flex items-center gap-2">
                <a href={selected.path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF8F2] text-[#5C5852] text-[12px] font-semibold hover:text-[#F26B1A]">
                  <ExternalLink className="w-3.5 h-3.5" /> Vorschau
                </a>
                <button type="button" onClick={() => deletePage(selected.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C03A1A]/10 text-[#C03A1A] text-[12px] font-semibold">
                  <Trash2 className="w-3.5 h-3.5" /> Loeschen
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Pfad</span>
                <input value={selected.path || ''} onChange={(e) => updatePage(['path'], slugifyPath(e.target.value))}
                  className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] font-mono" />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                <label className="block">
                  <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Vorlage</span>
                  <select value={selected.template || 'simple'} onChange={(e) => setPageTemplate(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] bg-white">
                    <option value="simple">Einfache Seite</option>
                    <option value={LAYOUT_EDITOR_TEMPLATE}>Layout-Editor</option>
                    <option value={HOME_BLUEPRINT_TEMPLATE}>Startseiten-Vorlage</option>
                  </select>
                </label>
                <label className="inline-flex items-center gap-2 text-[13px] text-[#1F1D1A] pb-3">
                <input type="checkbox" checked={selected.published !== false} onChange={(e) => updatePage(['published'], e.target.checked)}
                  className="w-4 h-4 accent-[#F26B1A]" />
                Veröffentlicht
              </label>
              </div>
            </div>

            <div className="bg-[#FAF8F2] rounded-xl p-1 inline-flex gap-1">
              {['nl', 'de'].map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)}
                  className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold ${lang === l ? 'bg-[#F26B1A] text-white' : 'text-[#5C5852]'}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <label className="block">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Titel</span>
              <input value={selected.translations?.[lang]?.title || ''} onChange={(e) => updatePage(['translations', lang, 'title'], e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
            </label>
            <label className="block">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Kurzbeschreibung</span>
              <textarea value={selected.translations?.[lang]?.excerpt || ''} onChange={(e) => updatePage(['translations', lang, 'excerpt'], e.target.value)}
                rows={2} className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] resize-none" />
            </label>
            {selected.template === LAYOUT_EDITOR_TEMPLATE && (
              <PageBlocksEditor
                selected={selected}
                pages={pages}
                lang={lang}
                overlay={overlay}
                onChange={onChange}
                updatePage={updatePage}
                mediaItems={items}
                mediaLoading={loading}
              />
            )}
            {(selected.template || 'simple') === 'simple' && (
              <>
                <label className="block">
                  <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Inhalt</span>
                  <RichHtmlEditor
                    value={selected.translations?.[lang]?.body || ''}
                    onChange={(html) => updatePage(['translations', lang, 'body'], html)}
                    testId="page-body-rich"
                  />
                </label>
                <div>
                  <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Header-Bild</span>
                  <MediaUrlField path={['pages', pages.findIndex((p) => p.id === selected.id), 'translations', lang, 'image']}
                    base="" overlay={overlay} onChange={onChange} testId="page-image" mediaItems={items} mediaLoading={loading} />
                </div>
              </>
            )}
            {selected.template === HOME_BLUEPRINT_TEMPLATE && (
              <BlueprintSectionsEditor
                selected={selected}
                pages={pages}
                lang={lang}
                overlay={overlay}
                onChange={onChange}
                updatePage={updatePage}
                mediaItems={items}
                mediaLoading={loading}
              />
            )}
            <div className="border-t border-[#E5E0D2] pt-5 space-y-4">
              <p className="font-display text-[17px] text-[#1F1D1A]">SEO fuer diese Unterseite</p>
              <label className="block">
                <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">SEO Title</span>
                <input value={selected.seo?.[lang]?.title || ''} onChange={(e) => updatePage(['seo', lang, 'title'], e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
              </label>
              <label className="block">
                <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Meta Description</span>
                <textarea value={selected.seo?.[lang]?.description || ''} onChange={(e) => updatePage(['seo', lang, 'description'], e.target.value)}
                  rows={2} className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] resize-none" />
              </label>
              <label className="inline-flex items-center gap-2 text-[13px] text-[#1F1D1A]">
                <input type="checkbox" checked={selected.seo?.[lang]?.noindex === true} onChange={(e) => updatePage(['seo', lang, 'noindex'], e.target.checked)}
                  className="w-4 h-4 accent-[#F26B1A]" />
                noindex
              </label>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function navLabel(item, lang = 'nl') {
  if (typeof item?.label === 'string') return item.label;
  return item?.label?.[lang] || item?.label?.nl || item?.label?.de || item?.path || 'Menüpunkt';
}

function makeNavItem(seed = {}) {
  return {
    id: seed.id || `nav-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
    path: seed.path || '/',
    label: {
      nl: seed.label?.nl || seed.label || 'Nieuwe link',
      de: seed.label?.de || seed.label || 'Neuer Link',
    },
    hidden: seed.hidden === true,
    children: Array.isArray(seed.children) ? seed.children : [],
  };
}

function pageToNavItem(page) {
  return makeNavItem({
    path: page.path || '/',
    label: {
      nl: page.translations?.nl?.title || page.path || 'Pagina',
      de: page.translations?.de?.title || page.translations?.nl?.title || page.path || 'Seite',
    },
  });
}

function MegamenuTab({ overlay, onChange }) {
  const pages = Array.isArray(overlay.pages) ? overlay.pages : [];
  const navigation = Array.isArray(overlay.navigation) ? overlay.navigation : [];
  const [selectedIndex, setSelectedIndex] = useState(navigation.length ? 0 : -1);
  const [lang, setLang] = useState('nl');
  const selected = selectedIndex >= 0 ? navigation[selectedIndex] : null;

  useEffect(() => {
    if (navigation.length === 0) setSelectedIndex(-1);
    else if (selectedIndex < 0 || selectedIndex >= navigation.length) setSelectedIndex(0);
  }, [navigation.length, selectedIndex]);

  const write = (next) => onChange({ ...overlay, navigation: next });
  const addTop = () => {
    const item = makeNavItem();
    write([...navigation, item]);
    setSelectedIndex(navigation.length);
  };
  const importPages = () => {
    const items = pages
      .filter((page) => page?.published !== false && page?.path)
      .map(pageToNavItem);
    write(items);
    setSelectedIndex(items.length ? 0 : -1);
  };
  const updateTop = (path, value) => {
    write(navigation.map((item, idx) => idx === selectedIndex ? setAt(item, path, value) : item));
  };
  const deleteTop = () => {
    if (selectedIndex < 0 || !window.confirm('Diesen Menüpunkt wirklich löschen?')) return;
    const next = navigation.filter((_, idx) => idx !== selectedIndex);
    write(next);
    setSelectedIndex(Math.min(selectedIndex, next.length - 1));
  };
  const moveTop = (dir) => {
    const target = selectedIndex + dir;
    if (target < 0 || target >= navigation.length) return;
    const next = [...navigation];
    const [item] = next.splice(selectedIndex, 1);
    next.splice(target, 0, item);
    write(next);
    setSelectedIndex(target);
  };
  const addChild = () => {
    if (!selected) return;
    const child = makeNavItem({ path: '/neue-seite', label: { nl: 'Nieuwe subpagina', de: 'Neue Unterseite' } });
    updateTop(['children'], [...(selected.children || []), child]);
  };
  const updateChild = (childIndex, path, value) => {
    const children = [...(selected.children || [])];
    children[childIndex] = setAt(children[childIndex], path, value);
    updateTop(['children'], children);
  };
  const deleteChild = (childIndex) => {
    updateTop(['children'], (selected.children || []).filter((_, idx) => idx !== childIndex));
  };

  return (
    <div data-testid="content-tab-navigation" className="grid grid-cols-1 xl:grid-cols-12 gap-4 min-h-0 xl:h-full xl:overflow-hidden">
      <aside className="xl:col-span-3 bg-white rounded-2xl border border-[#E5E0D2] overflow-hidden xl:h-full min-h-0 flex flex-col">
        <div className="p-3 border-b border-[#E5E0D2] flex items-start justify-between gap-2 shrink-0">
          <div>
            <p className="font-display text-[17px] text-[#1F1D1A]">Megamenü</p>
            <p className="text-[11.5px] text-[#5C5852] mt-1">Top-Level und Unterpunkte.</p>
          </div>
          <button type="button" onClick={addTop} className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F26B1A] text-white hover:bg-[#D85A0A]">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="p-2 space-y-1 flex-1 min-h-0 overflow-y-auto">
          {navigation.length === 0 ? (
            <p className="p-4 text-[12.5px] text-[#5C5852]">Noch keine Menüpunkte.</p>
          ) : navigation.map((item, idx) => (
            <button key={item.id || idx} type="button" onClick={() => setSelectedIndex(idx)}
              className={`w-full text-left px-3 py-2 rounded-lg text-[12.5px] transition-colors ${selectedIndex === idx ? 'bg-[#F26B1A] text-white' : 'hover:bg-[#FAF8F2] text-[#1F1D1A]'}`}>
              <span className="flex items-center justify-between gap-2">
                <span className="font-semibold truncate">{navLabel(item, lang)}</span>
                <span className={`w-2 h-2 rounded-full ${item.hidden ? 'bg-[#C03A1A]' : 'bg-[#7FC242]'}`} />
              </span>
              <span className={`block font-mono text-[10.5px] truncate ${selectedIndex === idx ? 'text-white/70' : 'text-[#5C5852]'}`}>{item.path}</span>
              {item.children?.length > 0 && (
                <span className={`block text-[10.5px] mt-0.5 ${selectedIndex === idx ? 'text-white/70' : 'text-[#5C5852]'}`}>{item.children.length} Unterpunkte</span>
              )}
            </button>
          ))}
        </div>
      </aside>

      <section className="xl:col-span-9 bg-white rounded-2xl border border-[#E5E0D2] p-5 xl:h-full min-h-0 xl:overflow-y-auto overscroll-contain">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <div>
            <p className="font-display text-[20px] text-[#1F1D1A]">{selected ? navLabel(selected, lang) : 'Megamenü bearbeiten'}</p>
            <p className="text-[11.5px] text-[#5C5852]">Die Website-Navigation liest diese Struktur direkt aus dem Content-Overlay.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={importPages} className="pill">
              <PanelTop className="w-4 h-4" /> Aus Seiten übernehmen
            </button>
            <div className="bg-[#FAF8F2] rounded-xl p-1 inline-flex gap-1">
              {['nl', 'de'].map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)}
                  className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold ${lang === l ? 'bg-[#F26B1A] text-white' : 'text-[#5C5852]'}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {!selected ? (
          <div className="p-10 text-center">
            <p className="text-[14px] text-[#5C5852] mb-4">Lege den ersten Menüpunkt an oder übernimm veröffentlichte Seiten.</p>
            <button type="button" onClick={addTop} className="pill pill-leaf"><Plus className="w-4 h-4" /> Menüpunkt</button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
              <div className="xl:col-span-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SmallTextField label={`Label ${lang.toUpperCase()}`} value={selected.label?.[lang] || ''} onChange={(v) => updateTop(['label', lang], v)} />
                <SmallTextField label="Pfad / URL" value={selected.path || ''} onChange={(v) => updateTop(['path'], v.startsWith('http') ? v : slugifyPath(v))} />
              </div>
              <div className="xl:col-span-4 rounded-xl bg-[#FAF8F2] p-3 flex items-center justify-end gap-2 flex-wrap">
                <label className="inline-flex items-center gap-2 text-[12px] text-[#5C5852] mr-auto">
                  <input type="checkbox" checked={selected.hidden === true} onChange={(e) => updateTop(['hidden'], e.target.checked)}
                    className="w-4 h-4 accent-[#F26B1A]" />
                  ausblenden
                </label>
                <button type="button" onClick={() => moveTop(-1)} disabled={selectedIndex <= 0} className="p-2 rounded-full hover:bg-white disabled:opacity-25"><ArrowUp className="w-4 h-4" /></button>
                <button type="button" onClick={() => moveTop(1)} disabled={selectedIndex >= navigation.length - 1} className="p-2 rounded-full hover:bg-white disabled:opacity-25"><ArrowDown className="w-4 h-4" /></button>
                <button type="button" onClick={deleteTop} className="p-2 rounded-full text-[#C03A1A] hover:bg-[#C03A1A]/10"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="border-t border-[#E5E0D2] pt-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <p className="font-display text-[17px] text-[#1F1D1A]">Unterpunkte</p>
                  <p className="text-[11.5px] text-[#5C5852]">Diese erscheinen im Dropdown/Megamenü des Hauptpunktes.</p>
                </div>
                <button type="button" onClick={addChild} className="pill pill-leaf"><Plus className="w-4 h-4" /> Unterpunkt</button>
              </div>

              <div className="space-y-2">
                {(selected.children || []).length === 0 ? (
                  <p className="p-5 rounded-xl bg-[#FAF8F2] text-[13px] text-[#5C5852]">Noch keine Unterpunkte.</p>
                ) : selected.children.map((child, idx) => (
                  <div key={child.id || idx} className="grid grid-cols-1 xl:grid-cols-12 gap-3 rounded-xl border border-[#E5E0D2] p-3">
                    <div className="xl:col-span-5">
                      <SmallTextField label={`Label ${lang.toUpperCase()}`} value={child.label?.[lang] || ''} onChange={(v) => updateChild(idx, ['label', lang], v)} />
                    </div>
                    <div className="xl:col-span-5">
                      <SmallTextField label="Pfad / URL" value={child.path || ''} onChange={(v) => updateChild(idx, ['path'], v.startsWith('http') ? v : slugifyPath(v))} />
                    </div>
                    <div className="xl:col-span-2 flex items-end justify-end gap-2">
                      <label className="inline-flex items-center gap-1.5 text-[12px] text-[#5C5852] pb-3">
                        <input type="checkbox" checked={child.hidden === true} onChange={(e) => updateChild(idx, ['hidden'], e.target.checked)}
                          className="w-4 h-4 accent-[#F26B1A]" />
                        aus
                      </label>
                      <button type="button" onClick={() => deleteChild(idx)} className="mb-2 p-2 rounded-full text-[#C03A1A] hover:bg-[#C03A1A]/10"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function RawJsonTab({ overlay, onChange }) {
  const [text, setText] = useState(() => JSON.stringify(overlay, null, 2));
  const [error, setError] = useState(null);
  const lastJsonRef = useRef(JSON.stringify(overlay));
  useEffect(() => {
    const ser = JSON.stringify(overlay);
    if (ser !== lastJsonRef.current) {
      lastJsonRef.current = ser;
      setText(JSON.stringify(overlay, null, 2));
      setError(null);
    }
  }, [overlay]);
  const onBlur = () => {
    try {
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object') throw new Error('Wurzel muss ein Objekt sein.');
      setError(null);
      onChange(parsed);
    } catch (e) { setError(e.message); }
  };
  return (
    <div data-testid="content-tab-raw">
      <p className="text-[12px] text-[#5C5852] mb-2">
        Sparses Overlay (nur gesetzte Felder). Beim Verlassen des Feldes wird validiert.
      </p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} onBlur={onBlur}
        data-testid="content-raw-textarea" spellCheck={false}
        className="w-full font-mono text-[12.5px] p-3 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none"
        style={{ minHeight: '60vh' }} />
      {error && <p data-testid="content-raw-err" className="mt-2 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}
    </div>
  );
}


// ============================================================================
// Page shell
// ============================================================================

export default function AdminContentPage() {
  const [overlay, setOverlay] = useState(null);
  const [pristine, setPristine] = useState(null);
  const [tab, setTab] = useState('translations');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const fetchOverlay = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/admin/content');
      // Strip server-managed fields before storing locally.
      const { updated_at, updated_by, ...payload } = data || {};
      // Make sure we always have shapes for translations/company/media.
      const norm = {
        translations: payload.translations || {},
        company: payload.company || {},
        media: payload.media || {},
        seo: payload.seo || {},
        pages: Array.isArray(payload.pages) ? payload.pages : [],
        navigation: Array.isArray(payload.navigation) ? payload.navigation : [],
      };
      setOverlay(norm);
      setPristine(JSON.stringify(norm));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchOverlay(); }, []);

  const dirty = overlay && JSON.stringify(overlay) !== pristine;

  const save = async () => {
    setError(null); setBusy(true);
    try {
      // Prune empty top-level containers so we don't store {translations:{}}.
      const payload = {};
      ['translations', 'company', 'media', 'seo'].forEach((k) => {
        if (overlay[k] && Object.keys(overlay[k]).length > 0) payload[k] = overlay[k];
      });
      if (Array.isArray(overlay.pages) && overlay.pages.length > 0) payload.pages = overlay.pages;
      if (Array.isArray(overlay.navigation) && overlay.navigation.length > 0) payload.navigation = overlay.navigation;
      await adminApi.put('/admin/content', payload);
      setPristine(JSON.stringify(overlay));
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const revert = () => { if (window.confirm('Alle ungespeicherten Änderungen verwerfen?')) fetchOverlay(); };

  const tabBody = useMemo(() => {
    if (!overlay) return null;
    switch (tab) {
      case 'translations': return <TranslationsTab overlay={overlay} onChange={setOverlay} />;
      case 'company':      return <CompanyTab      overlay={overlay} onChange={setOverlay} />;
      case 'media':        return <MediaTab        overlay={overlay} onChange={setOverlay} />;
      case 'seo':          return <SeoTab          overlay={overlay} onChange={setOverlay} />;
      case 'pages':        return <PagesTab        overlay={overlay} onChange={setOverlay} />;
      case 'navigation':   return <MegamenuTab    overlay={overlay} onChange={setOverlay} />;
      case 'raw':          return <RawJsonTab      overlay={overlay} onChange={setOverlay} />;
      default: return null;
    }
  }, [tab, overlay]);
  const workspaceLayout = tab === 'pages' || tab === 'navigation';

  return (
    <div data-testid="admin-content-page" className={`w-full max-w-none min-h-0 ${workspaceLayout ? 'h-full flex flex-col overflow-hidden' : ''}`}>
      <div className={`z-50 -mx-3 md:-mx-4 lg:-mx-5 px-3 md:px-4 lg:px-5 py-2 bg-[#FAF8F2]/95 backdrop-blur border-b border-[#E5E0D2] shadow-[0_16px_30px_-30px_rgba(31,29,26,0.45)] ${workspaceLayout ? 'shrink-0 mb-3' : 'sticky top-[56px] md:top-0 mb-4'}`}>
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex w-8 h-8 rounded-full bg-[#F26B1A]/10 text-[#F26B1A] items-center justify-center">
              <FileText className="w-4 h-4" />
            </span>
            <h1 className="font-display text-[20px] sm:text-[22px] lg:text-[24px] text-[#1F1D1A] leading-none">Inhalte</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a href={`${window.location.origin}/`} target="_blank" rel="noopener noreferrer"
              data-testid="content-preview"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E0D2] text-[12px] font-semibold text-[#5C5852] hover:border-[#F26B1A] hover:text-[#F26B1A] transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> Live-Vorschau
            </a>
            {dirty && (
              <button type="button" onClick={revert} data-testid="content-revert"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E0D2] text-[12px] font-semibold text-[#5C5852] hover:border-[#C03A1A] hover:text-[#C03A1A] transition-colors">
                <RefreshCcw className="w-3.5 h-3.5" /> Verwerfen
              </button>
            )}
            <button type="button" onClick={save} disabled={!dirty || busy} data-testid="content-save"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#F26B1A] text-white text-[12.5px] font-semibold hover:bg-[#D85A0A] disabled:opacity-40 transition-colors">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Speichern
            </button>
            {saved && <span className="inline-flex items-center gap-1.5 text-[#7FC242] text-[12.5px]"><CheckCircle2 className="w-4 h-4" /> Gespeichert</span>}
          </div>
        </div>

        {error && <p data-testid="content-err" className="mb-2 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}

        <div className="bg-white rounded-xl border border-[#E5E0D2] p-1 inline-flex gap-1 flex-wrap max-w-full overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.Icon;
            return (
              <button key={t.id} type="button" onClick={() => setTab(t.id)}
                data-testid={`content-tab-btn-${t.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold transition-colors whitespace-nowrap ${
                  tab === t.id ? 'bg-[#F26B1A] text-white' : 'text-[#5C5852] hover:bg-[#FAF8F2]'
                }`}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#5C5852]"><Loader2 className="inline w-5 h-5 animate-spin" /></div>
      ) : (
        <div className={workspaceLayout ? 'flex-1 min-h-0 overflow-hidden' : ''}>{tabBody}</div>
      )}
    </div>
  );
}
